// ============================================================
// flea.jsx — Trödel-Modus (type='flea' Stores)
// ES-Module: importiert von theme, modal-utils, widgets, sync, store-sheets.
// ============================================================
import { useState as useStateF, useEffect as useEffectF, useRef as useRefF, useMemo as useMemoF } from 'react';
import { F, T, Icon, ACCENTS, haptic } from './theme.jsx';
import { useModalViewport, modalShellStyle, ModalCloseButton, ModalHeader, bottomSheetShellStyle, BottomSheetHeader } from './modal-utils.jsx';
import { BottomSheet, useValidation, ConfirmSheet, ActionMenu, fmtCurrency, apiCall, computeBaseStats, SwipeToDelete, SkeletonListItem } from './widgets.jsx';
import { SyncIndicator, syncManager } from './sync.jsx';
import { ConfirmDeleteStoreSheet, ModeMembersSheet, SettingsSectionLabel, SettingsCard, SettingsRow, SettingsToggle, SettingsLinkRow, SettingsToggleRow, exportSingleStoreCsv } from './store-sheets.jsx';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmtDateShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};
const fmtMonth = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
};
const fmtSince = (iso) => {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "gerade gestartet";
  if (min < 60) return `seit ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `seit ${h} Std`;
  return `seit ${Math.floor(h / 24)} Tag${Math.floor(h / 24) === 1 ? "" : "en"}`;
};
const fmtDuration = (startIso, endIso) => {
  if (!startIso) return "";
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const ms = end - new Date(startIso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const restMin = min % 60;
  return restMin === 0 ? `${h} Std` : `${h} Std ${restMin} min`;
};

// Berechnet Statistik aus Sales-Array (z.B. einer aktiven Session)
function computeSessionStats(sales) {
  const base = computeBaseStats(sales);
  const wishSum = base.active.reduce((s, x) => {
    const items = x.sale_items || [];
    return s + items.reduce((ss, i) => ss + (Number(i.wish_price) || 0), 0);
  }, 0);
  return {
    ...base,
    wishSum,
    diff: base.total - wishSum,
    diffPct: wishSum > 0 ? Math.round(((base.total - wishSum) / wishSum) * 100) : 0,
  };
}

// ─────────────────────────────────────────────────────────────
// FleaHub — Top-Level. Lädt items, sessions, activeSession parallel.
// Wenn activeSession existiert: render FleaActiveSession.
// Sonst: Tab-Layout (Sessions / Inventory / Stats).
// ─────────────────────────────────────────────────────────────
function FleaHub({ store, authToken, onBack, onStoreDeleted, dark, showToast, userPreferences, saveUserPreferences }) {
  const t = T[dark ? "dark" : "light"];
  const [loading, setLoading] = useStateF(true);
  const [items, setItems] = useStateF([]);
  const [hasMoreItems, setHasMoreItems] = useStateF(false);
  const [itemsCursor, setItemsCursor] = useStateF(null);
  const [sessions, setSessions] = useStateF([]);
  const [hasMoreSessions, setHasMoreSessions] = useStateF(false);
  const [sessionsCursor, setSessionsCursor] = useStateF(null);
  const [categories, setCategories] = useStateF([]); // Migration 010
  const [activeSession, setActiveSession] = useStateF(null);
  const [tab, setTab] = useStateF("sessions"); // 'sessions' | 'inventory' | 'stats'
  const [viewingSessionId, setViewingSessionId] = useStateF(null);
  // Wenn ein Markt läuft, ist die Default-Ansicht FleaActiveSession.
  // showHubDuringSession=true zwingt den Hub-Layout (Tabs) trotzdem rein —
  // damit User mid-Session ins Inventar/Stats kann.
  const [showHubDuringSession, setShowHubDuringSession] = useStateF(false);
  const [settingsOpen, setSettingsOpen] = useStateF(false);
  const [error, setError] = useStateF(null);
  // P-5-Punkte-Fix Block 5: PTR-State (manuelles Pull-to-Refresh)
  const [refreshing, setRefreshing] = useStateF(false);
  const ptrStartY = useRefF(null);
  const [ptrDistance, setPtrDistance] = useStateF(0);

  const apiBase = `/api/stores/${store.id}`;

  const reloadAll = async () => {
    setLoading(true); setError(null);
    try {
      // Sessions mit include_stats=1 (P-Polish 1.3): liefert nested sales-Felder
      // (total, is_voided, is_bundle) für client-side Aggregation.
      // Categories laden (Migration 010 / P-Polish Block 2).
      const [itemsR, sessionsR, activeR, categoriesR] = await Promise.all([
        apiCall(authToken, `${apiBase}/items`),
        apiCall(authToken, `${apiBase}/sessions?include_stats=1`),
        apiCall(authToken, `${apiBase}/sessions/active`),
        apiCall(authToken, `${apiBase}/flea-categories`).catch(() => []),
      ]);
      // Sessions + Items kommen jetzt paginiert: { sessions/items, has_more, next_cursor }
      const itemsArr = Array.isArray(itemsR) ? itemsR : (itemsR?.items || []);
      setItems(itemsArr);
      setHasMoreItems(!!itemsR?.has_more);
      setItemsCursor(itemsR?.next_cursor || null);

      const sessionsArr = Array.isArray(sessionsR) ? sessionsR : (sessionsR?.sessions || []);
      setSessions(sessionsArr);
      setHasMoreSessions(!!sessionsR?.has_more);
      setSessionsCursor(sessionsR?.next_cursor || null);

      setActiveSession(activeR);
      setCategories(categoriesR || []);
    } catch (e) {
      console.warn("FleaHub reload failed:", e);
      setError(e.message || "Lade-Fehler");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSessions = async () => {
    if (!sessionsCursor) return;
    try {
      const r = await apiCall(authToken, `${apiBase}/sessions?include_stats=1&before=${encodeURIComponent(sessionsCursor)}`);
      const arr = Array.isArray(r) ? r : (r?.sessions || []);
      setSessions(prev => [...prev, ...arr]);
      setHasMoreSessions(!!r?.has_more);
      setSessionsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreSessions failed:", e); }
  };

  const loadMoreItems = async () => {
    if (!itemsCursor) return;
    try {
      const r = await apiCall(authToken, `${apiBase}/items?before=${encodeURIComponent(itemsCursor)}`);
      const arr = Array.isArray(r) ? r : (r?.items || []);
      setItems(prev => [...prev, ...arr]);
      setHasMoreItems(!!r?.has_more);
      setItemsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreItems failed:", e); }
  };

  useEffectF(() => { reloadAll(); /* eslint-disable-next-line */ }, [store?.id]);

  // Body-Scroll-Lock + reset scroll position so header is always visible
  useEffectF(() => {
    window.scrollTo(0, 0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Aktive Session vorhanden? → FleaActiveSession render
  // (außer User hat explizit auf Hub-Tabs umgeschaltet via showHubDuringSession)
  if (activeSession && !viewingSessionId && !showHubDuringSession) {
    return (
      <FleaActiveSession
        store={store} authToken={authToken} dark={dark} t={t}
        session={activeSession}
        items={items}
        onSessionChanged={(updatedSession) => {
          if (updatedSession.ends_at) {
            // Markt wurde beendet → reload und zurück zum Hub
            setActiveSession(null);
            setShowHubDuringSession(false);
            reloadAll();
          } else {
            setActiveSession(updatedSession);
          }
        }}
        onItemsChanged={(newItems) => setItems(newItems)}
        showToast={showToast}
        // P-Polish 1.12: Back-Button geht jetzt zur Landing (onBack), nicht
        // mehr zum Hub. Der Hub bleibt mid-Session über das Drei-Punkte-Menü
        // erreichbar (P-Polish 1.6: dort wandert auch "Items hinzunehmen").
        onBackToLanding={onBack}
        onShowHub={() => setShowHubDuringSession(true)}
      />
    );
  }

  // Detail-View einer vergangenen Session
  if (viewingSessionId) {
    return (
      <FleaSessionDetail
        store={store} authToken={authToken} dark={dark} t={t}
        sessionId={viewingSessionId}
        items={items}
        showToast={showToast}
        onBack={() => setViewingSessionId(null)}
        onSessionChanged={() => reloadAll()}
      />
    );
  }

  // Hub-Tab-Layout
  const bg = dark ? "#0d1117" : "#dde8f8";
  return (
    <div style={{
      // 100dvh = dynamic viewport height (Mobile: berücksichtigt URL-Bar)
      // height (nicht minHeight) damit der innere flex:1 + overflowY:auto Container
      // eine bounded height bekommt und tatsächlich scrollen kann.
      height: "100dvh", maxHeight: "100dvh",
      background: bg, fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Sticky Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <button onClick={onBack} aria-label="Zurück" style={{
            width: 30, height: 30, borderRadius: 14, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.text, letterSpacing: "-0.4px" }}>Trödel</div>
            <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>{store.name}</div>
          </div>
          <button onClick={() => { setSettingsOpen(true); haptic(8); }} aria-label="Einstellungen" style={{
            width: 30, height: 30, borderRadius: 15, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>

        {/* Live-Banner: Markt läuft → tap to return */}
        {activeSession && showHubDuringSession && (
          <button onClick={() => { setShowHubDuringSession(false); haptic(8); }} style={{
            width: "100%", marginBottom: 12, padding: "10px 12px",
            background: "linear-gradient(180deg, #BF5AF2 0%, #9F3DD2 100%)",
            border: "none", borderRadius: 12,
            color: "#fff", fontFamily: F, fontWeight: 600, fontSize: 13,
            cursor: "pointer", textAlign: "left",
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: "0 4px 14px rgba(191,90,242,0.35)",
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: "#fff",
              animation: "pulse 1.6s ease-in-out infinite", flexShrink: 0,
            }}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, letterSpacing: "0.04em" }}>
                MARKT LÄUFT · {activeSession.location || "Trödel"}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>
                Tippen um zurückzukehren · {fmtSince(activeSession.starts_at)}
              </div>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M5 2L11 7L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Tab-Bar */}
        <div style={{
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          borderRadius: 12, padding: 3,
          display: "flex", gap: 4,
        }}>
          {[
            { id: "sessions",  label: `Markt-Tage${sessions.length ? ` · ${sessions.length}` : ""}` },
            { id: "inventory", label: `Inventar${items.length ? ` · ${items.length}` : ""}` },
            { id: "stats",     label: "Statistik" },
          ].map(tb => {
            const active = tab === tb.id;
            return (
              <button key={tb.id} onClick={() => { setTab(tb.id); haptic(6); }} style={{
                flex: 1, padding: "8px 6px", border: "none", cursor: "pointer", fontFamily: F,
                fontSize: 13, fontWeight: active ? 700 : 500, borderRadius: 9,
                background: active ? (dark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.95)") : "transparent",
                color: active ? t.text : t.textSub,
                boxShadow: active ? `0 1px 4px rgba(0,0,0,${dark ? 0.3 : 0.08})` : "none",
                transition: "all 0.16s ease",
              }}>{tb.label}</button>
            );
          })}
        </div>
      </div>

      {/* Tab-Content (mit Pull-to-Refresh — P-5-Punkte-Fix Block 5) */}
      <div
        style={{
          flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
          padding: "16px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)",
          transform: ptrDistance > 0 ? `translateY(${Math.min(ptrDistance, 90)}px)` : "translateY(0)",
          transition: ptrDistance === 0 ? "transform 0.2s cubic-bezier(0.34,1.2,0.64,1)" : "none",
        }}
        onTouchStart={(e) => {
          if (e.currentTarget.scrollTop <= 0) ptrStartY.current = e.touches[0].clientY;
        }}
        onTouchMove={(e) => {
          if (ptrStartY.current === null) return;
          const dy = e.touches[0].clientY - ptrStartY.current;
          if (dy > 0 && e.currentTarget.scrollTop <= 0) setPtrDistance(Math.min(dy, 120));
          else if (dy < 0) { ptrStartY.current = null; setPtrDistance(0); }
        }}
        onTouchEnd={async () => {
          if (ptrDistance >= 70 && !refreshing) {
            setRefreshing(true);
            try { await reloadAll(); } finally { setRefreshing(false); }
            haptic(15);
          }
          setPtrDistance(0);
          ptrStartY.current = null;
        }}
      >
        {/* PTR-Indicator */}
        {(ptrDistance > 0 || refreshing) && (
          <div style={{
            position: "absolute", top: 0, left: "50%", transform: `translateX(-50%) translateY(${Math.min(ptrDistance/2, 30)}px)`,
            color: t.textMuted, fontSize: 14, zIndex: 1,
            transition: refreshing ? "transform 0.2s" : "none",
          }}>
            {refreshing ? "↻" : ptrDistance >= 70 ? "↑ Loslassen" : "↓ Ziehen"}
          </div>
        )}
        {loading && sessions.length === 0 && items.length === 0 ? (
          // P-Sheets-Skeleton: Skeleton-List während initial Load
          (() => {
            return (
              <div style={{
                background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
                border: `1px solid ${t.glassBorder}`, borderRadius: 12, overflow: "hidden",
              }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} style={{ borderTop: i === 0 ? "none" : `0.5px solid ${t.divider}` }}>
                    <SkeletonListItem t={t} dark={dark} />
                  </div>
                ))}
              </div>
            );
          })()
        ) : error ? (
          <div style={{ textAlign: "center", color: t.accent.red.solid, padding: 40, fontSize: 14 }}>
            {error}
            <div style={{ marginTop: 12 }}>
              <button onClick={reloadAll} style={{
                background: t.accent.blue.solid, color: "#fff", border: "none",
                borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontFamily: F, fontWeight: 600, fontSize: 13,
              }}>Neu laden</button>
            </div>
          </div>
        ) : tab === "sessions" ? (
          <>
            <FleaSessionsList
              store={store} authToken={authToken} dark={dark} t={t}
              sessions={sessions}
              items={items}
              activeSession={activeSession}
              onResumeActive={() => setShowHubDuringSession(false)}
              onSessionStarted={(newSession) => {
                setActiveSession(newSession);
                setSessions([newSession, ...sessions]);
              }}
              onSessionDeleted={(sid) => {
                setSessions(prev => prev.filter(x => x.id !== sid));
              }}
              onOpenSession={(sid) => setViewingSessionId(sid)}
              showToast={showToast}
            />
            {hasMoreSessions && (
              <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
                <button onClick={loadMoreSessions} style={{
                  background: "none", border: `1px solid ${t.glassBorder}`,
                  borderRadius: 10, padding: "8px 20px", cursor: "pointer",
                  color: t.textMuted, fontFamily: F, fontSize: 13, fontWeight: 600,
                }}>Mehr laden</button>
              </div>
            )}
          </>
        ) : tab === "inventory" ? (
          <>
            <FleaInventoryList
              store={store} authToken={authToken} dark={dark} t={t}
              items={items}
              categories={categories}
              onCategoriesChanged={(newCats) => setCategories(newCats)}
              onItemsChanged={(newItems) => setItems(newItems)}
              showToast={showToast}
            />
            {hasMoreItems && (
              <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
                <button onClick={loadMoreItems} style={{
                  background: "none", border: `1px solid ${t.glassBorder}`,
                  borderRadius: 10, padding: "8px 20px", cursor: "pointer",
                  color: t.textMuted, fontFamily: F, fontSize: 13, fontWeight: 600,
                }}>Mehr laden</button>
              </div>
            )}
          </>
        ) : (
          <FleaStats
            sessions={sessions}
            items={items}
            dark={dark} t={t}
          />
        )}
      </div>

      <FleaSettings
        isOpen={settingsOpen}
        store={store} authToken={authToken} dark={dark} t={t}
        sessionsCount={sessions.length}
        itemsCount={items.length}
        categories={categories}
        onCategoriesChanged={(newCats) => setCategories(newCats)}
        userPreferences={userPreferences}
        saveUserPreferences={saveUserPreferences}
        onClose={() => setSettingsOpen(false)}
        onStoreDeleted={onStoreDeleted || onBack}
        showToast={showToast}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSettings — Trödel-spezifische Einstellungen (im Hub erreichbar)
// ─────────────────────────────────────────────────────────────
function FleaSettings({ isOpen, store, authToken, dark, t, sessionsCount, itemsCount, categories, onCategoriesChanged, userPreferences, saveUserPreferences, onClose, onStoreDeleted, showToast }) {
  const [exporting, setExporting] = useStateF(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useStateF(false);
  // Kategorien-Verwaltung (P-Polish Block 2.4)
  const [categoriesPanelOpen, setCategoriesPanelOpen] = useStateF(false);
  // Mitglieder-Verwaltung (P-5-Punkte-Fix Block 5)
  const [membersPanelOpen, setMembersPanelOpen] = useStateF(false);

  // Shared iOS-style settings building blocks
  const BS  = BottomSheet;
  const SLb = SettingsSectionLabel;
  const SCa = SettingsCard;
  const SRo = SettingsRow;
  const STo = SettingsToggle;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportSingleStoreCsv(store, authToken);
      showToast("Export heruntergeladen");
    } catch (e) {
      showToast(e.message || "Export fehlgeschlagen", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleDestroyStore = async () => {
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.reason || "Löschen fehlgeschlagen", "error");
        return;
      }
      showToast("Trödel-Bereich gelöscht");
      onStoreDeleted();
    } catch (e) {
      showToast("Netzwerkfehler", "error");
    }
  };

  // Wenn die Shared-Helper noch nicht geladen sind: Fallback-Render mit
  // null (nichts anzeigen). In Praxis nicht relevant da widgets.jsx vor
  // flea.jsx geladen wird.
  if (!BS || !SLb || !SCa || !SRo || !STo) return null;

  return (
    <>
      <BS isOpen={isOpen} onClose={onClose} title="Trödel-Einstellungen" subtitle={store.name} dark={dark} t={t} F={F}>
        <SLb t={t}>Inventar</SLb>
        <SCa dark={dark} t={t}>
          <SRo isFirst label="Items" value={String(itemsCount)} t={t} dark={dark} F={F} />
          <SRo label="Markt-Tage" value={String(sessionsCount)} t={t} dark={dark} F={F} />
          <SRo
            label="Kategorien verwalten"
            value={categories ? `${categories.length}` : undefined}
            onClick={() => setCategoriesPanelOpen(true)}
            t={t} dark={dark} F={F}
          />
        </SCa>

        {/* Mitglieder-Verwaltung (P-5-Punkte-Fix Block 5) */}
        <SLb t={t}>Mitglieder</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Mitglieder verwalten"
            sub="Code generieren · Mitglieder entfernen"
            onClick={() => setMembersPanelOpen(true)}
            t={t} dark={dark} F={F}
          />
        </SCa>

        <SLb t={t}>Daten</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Trödel-Daten exportieren"
            sub={exporting ? "Exportiere…" : "ZIP mit Items, Sessions und Verkäufen"}
            onClick={!exporting ? handleExport : undefined}
            t={t} dark={dark} F={F}
          />
        </SCa>

        <SLb t={t}>Bereich</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Trödel-Bereich löschen"
            sub="Alle Daten werden permanent gelöscht"
            destructive
            onClick={() => setConfirmDeleteOpen(true)}
            t={t} dark={dark} F={F}
          />
        </SCa>
      </BS>

      {confirmDeleteOpen && (
        <ConfirmDeleteStoreSheet
          dark={dark} t={t}
          storeName={store.name}
          storeType="flea"
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={async () => { await handleDestroyStore(); setConfirmDeleteOpen(false); }}
        />
      )}

      {categoriesPanelOpen && (
        <FleaCategoriesPanel
          store={store} authToken={authToken} dark={dark} t={t}
          categories={categories || []}
          onCategoriesChanged={onCategoriesChanged}
          onClose={() => setCategoriesPanelOpen(false)}
          showToast={showToast}
        />
      )}

      {/* Mitglieder-Sheet (P-5-Punkte-Fix Block 5) */}
      <ModeMembersSheet
        isOpen={membersPanelOpen}
        store={store} authToken={authToken} dark={dark} t={t} showToast={showToast}
        onClose={() => setMembersPanelOpen(false)}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaCategoriesPanel — Kategorien-Verwaltung (P-Polish Block 2.4)
// Listet alle Kategorien des Stores. Default = nur Name editierbar,
// nicht löschbar. Custom = Name editierbar + löschbar.
// ─────────────────────────────────────────────────────────────
function FleaCategoriesPanel({ store, authToken, dark, t, categories, onCategoriesChanged, onClose, showToast }) {
  const [editingId, setEditingId] = useStateF(null);
  const [editingName, setEditingName] = useStateF("");
  const [confirmDeleteCat, setConfirmDeleteCat] = useStateF(null);
  const [adding, setAdding] = useStateF(false);
  const [busyId, setBusyId] = useStateF(null);

  const sortedCats = useMemoF(() => {
    return [...categories].sort((a, b) => {
      if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
      if (a.is_default && b.is_default) return (a.sort_order || 0) - (b.sort_order || 0);
      return (a.name || "").localeCompare(b.name || "", "de");
    });
  }, [categories]);

  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const saveEdit = async (cat) => {
    const newName = editingName.trim();
    if (!newName || newName === cat.name) { setEditingId(null); return; }
    setBusyId(cat.id);
    try {
      const updated = await apiCall(authToken, `/api/stores/${store.id}/flea-categories/${cat.id}`, {
        method: "PATCH", body: { name: newName },
      });
      onCategoriesChanged((categories || []).map(c => c.id === cat.id ? updated : c));
      showToast("Kategorie umbenannt");
      haptic(10);
    } catch (e) {
      showToast(e.message?.includes("409") ? "Name existiert bereits" : "Speichern fehlgeschlagen", "error");
    } finally {
      setBusyId(null);
      setEditingId(null);
    }
  };
  const deleteCat = async (cat) => {
    setBusyId(cat.id);
    try {
      await apiCall(authToken, `/api/stores/${store.id}/flea-categories/${cat.id}`, { method: "DELETE" });
      onCategoriesChanged((categories || []).filter(c => c.id !== cat.id));
      showToast(`Kategorie gelöscht · Items auf „Sonstiges" umgestellt`);
      haptic(10);
    } catch (e) {
      showToast("Löschen fehlgeschlagen", "error");
    } finally {
      setBusyId(null);
      setConfirmDeleteCat(null);
    }
  };

  return (
    <>
      <BottomSheet
        isOpen={true} onClose={onClose}
        size="large" title="Kategorien"
        subtitle={`${categories.length} Kategorien · ${categories.filter(c => c.is_default).length} Defaults`}
        dark={dark} t={t} F={F}
        stickyFooter={
          <button onClick={() => setAdding(true)} style={{
            width: "100%", padding: "12px",
            background: t.accent.purple.solid, color: "#fff",
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            boxShadow: `0 4px 14px ${t.accent.purple.weak}`,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M7 2v10M2 7h10"/>
            </svg>
            Neue Kategorie
          </button>
        }
      >
        <div style={{ padding: "8px 22px 0" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 12 }}>
            {sortedCats.map(c => {
              const isEditing = editingId === c.id;
              const busy = busyId === c.id;
              return (
                <div key={c.id} style={{
                  background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                  border: `1px solid ${t.glassBorder}`,
                  borderRadius: 11, padding: "10px 12px",
                  display: "flex", alignItems: "center", gap: 10,
                  opacity: busy ? 0.6 : 1,
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                    background: c.is_default ? t.accent.blue.weak : t.accent.purple.weak,
                    color: c.is_default ? t.accent.blue.solid : t.accent.purple.solid,
                    flexShrink: 0,
                    letterSpacing: "0.04em",
                  }}>{c.is_default ? "STANDARD" : "EIGEN"}</span>
                  {isEditing ? (
                    <input
                      autoFocus type="text" value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") setEditingId(null); }}
                      onBlur={() => saveEdit(c)}
                      style={{
                        flex: 1, minWidth: 0,
                        background: "transparent", border: `1px solid ${t.accent.purple.solid}`,
                        borderRadius: 6, padding: "4px 8px",
                        fontFamily: F, fontSize: 13, color: t.text, outline: "none",
                      }}
                    />
                  ) : (
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                  )}
                  {!isEditing && (
                    <>
                      <button onClick={() => startEdit(c)} disabled={busy} aria-label="Bearbeiten" style={{
                        width: 28, height: 28, borderRadius: 8, padding: 0, flexShrink: 0,
                        background: "transparent", border: "none",
                        color: t.textMuted, cursor: busy ? "default" : "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 2L12 5L5 12L2 12L2 9L9 2Z"/>
                        </svg>
                      </button>
                      {!c.is_default && (
                        <button onClick={() => setConfirmDeleteCat(c)} disabled={busy} aria-label="Löschen" style={{
                          width: 28, height: 28, borderRadius: 8, padding: 0, flexShrink: 0,
                          background: "transparent", border: "none",
                          color: t.accent.red.solid, cursor: busy ? "default" : "pointer",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                            <path d="M3 4h8M5 4V2h4v2M4 4v8M10 4v8M6 6v4M8 6v4"/>
                          </svg>
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </BottomSheet>

      {adding && (
        <FleaCategoryAddSheet
          store={store} authToken={authToken} dark={dark} t={t}
          onClose={() => setAdding(false)}
          onAdded={(newCat) => {
            setAdding(false);
            onCategoriesChanged([...(categories || []), newCat]);
          }}
          showToast={showToast}
        />
      )}

      {confirmDeleteCat && (
        <ConfirmSheet
          dark={dark} t={t}
          title={`Kategorie löschen: ${confirmDeleteCat.name}?`}
          warning={`Items mit dieser Kategorie werden auf „Sonstiges" umgestellt.`}
          confirmLabel="Löschen"
          confirmDestructive={true}
          onCancel={() => setConfirmDeleteCat(null)}
          onConfirm={async () => { await deleteCat(confirmDeleteCat); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSessionsList — Tab "Markt-Tage"
// ─────────────────────────────────────────────────────────────
function FleaSessionsList({ store, authToken, dark, t, sessions, items, activeSession, onResumeActive, onSessionStarted, onSessionDeleted, onOpenSession, showToast }) {
  const [startSheetOpen, setStartSheetOpen] = useStateF(false);
  // Tracker-Ref für SwipeToDelete (P-Swipe-Delete) — eine Card zur Zeit
  const swipeTracker = useRefF({ activeId: null, registry: {} });

  // Hard-Delete eines Markt-Tags (alle Sales + sale_items via CASCADE im Worker)
  const deleteSession = async (sessionId) => {
    const res = await fetch(`/api/stores/${store.id}/sessions/${sessionId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.reason || "Markt-Tag konnte nicht gelöscht werden");
    }
    // Parent informieren damit die Liste aktualisiert wird
    if (onSessionDeleted) onSessionDeleted(sessionId);
  };

  // Per-Session-Stats aus embedded sales (Worker liefert sie via include_stats=1).
  // P-Polish 1.3 / 1.9 / 1.15.
  const sessionStats = (s) => {
    const sales = (s.sales || []).filter(x => !x.is_voided);
    return {
      count: sales.length,
      total: sales.reduce((sum, x) => sum + Number(x.total || 0), 0),
    };
  };

  // P-Polish 1.9: sehr kurze Sessions (<1 min UND keine Verkäufe) immer ausfiltern.
  // Test-Daten / Fehlstarts sollen nie sichtbar sein.
  // P-Polish 1.5: aktive Session bleibt in der Liste, wird aber visuell ausgegraut
  //              wenn der LÄUFT-Banner sichtbar ist (= User ist im Hub-Modus
  //              während eine Session läuft).
  const filteredSessions = useMemoF(() => {
    return sessions.filter(s => {
      if (!s.ends_at) return true; // aktiv → immer anzeigen
      const stats = sessionStats(s);
      // Dauer in Minuten
      const dur = (new Date(s.ends_at).getTime() - new Date(s.starts_at).getTime()) / 60000;
      if (dur < 1 && stats.count === 0) return false;
      return true;
    });
  }, [sessions]);

  // Group sessions by month
  const grouped = useMemoF(() => {
    const byMonth = new Map();
    for (const s of filteredSessions) {
      const key = fmtMonth(s.starts_at);
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(s);
    }
    return [...byMonth.entries()];
  }, [filteredSessions]);

  // P-Polish 1.8: kompaktes Datum (nur Tag.Monat — Jahr ist im Monats-Header)
  const fmtCompactDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
  };

  return (
    <>
      {/* Großer "Neuer Markt-Tag"-Button */}
      <button onClick={() => { setStartSheetOpen(true); haptic(10); }} style={{
        width: "100%", padding: "16px",
        background: "linear-gradient(180deg, #BF5AF2 0%, #9F3DD2 100%)",
        border: "none", borderRadius: 14,
        color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px",
        cursor: "pointer", marginBottom: 18,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        boxShadow: "0 6px 22px rgba(191,90,242,0.4)",
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Neuer Markt-Tag
      </button>

      {filteredSessions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.textSub }}>
          {/* P-Polish 1.14: freundlicherer Empty-State */}
          <div style={{ fontSize: 36, marginBottom: 10 }}>🏷️</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 4 }}>
            Noch kein Markt-Tag
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5 }}>
            Tipp oben auf „Neuer Markt-Tag" um deinen ersten Trödel-Tag zu starten.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {grouped.map(([month, list]) => (
            <div key={month}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: t.textMuted,
                letterSpacing: "0.08em", textTransform: "uppercase",
                marginBottom: 8, paddingLeft: 4,
              }}>{month}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {list.map(s => {
                  const stats = sessionStats(s);
                  const isLive = !s.ends_at;
                  const isDuplicateOfBanner = isLive && activeSession?.id === s.id;
                  const noSales = !isLive && stats.count === 0;
                  // P-Polish 1.5 + P-Swipe-Delete:
                  // Aktive Session bleibt sichtbar (Spec) aber ist nicht löschbar.
                  // SwipeToDelete fängt mit `disabled` ab und macht Bounce + Toast.
                  const cardContent = (
                    <button onClick={() => {
                      if (isLive) {
                        onResumeActive && onResumeActive();
                      } else {
                        onOpenSession(s.id);
                      }
                    }} style={{
                      width: "100%",
                      background: dark ? "rgba(28,28,30,0.95)" : "rgba(255,255,255,0.95)",
                      border: `1px solid ${t.glassBorder}`,
                      borderRadius: 14, padding: "14px 16px",
                      cursor: "pointer", fontFamily: F, textAlign: "left",
                      display: "flex", alignItems: "center", gap: 12,
                      opacity: isDuplicateOfBanner ? 0.55 : 1,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>
                            {fmtCompactDate(s.starts_at)} · {fmtTime(s.starts_at)}
                            {s.location ? <span style={{ color: t.textSub, fontWeight: 500 }}> · {s.location}</span> : null}
                          </div>
                          {isLive && (
                            <div style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              fontSize: 10, fontWeight: 700,
                              color: t.accent.purple.solid,
                              background: t.accent.purple.weak,
                              padding: "2px 7px", borderRadius: 5,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent.purple.solid, animation: "pulse 1.6s ease-in-out infinite" }}/>
                              {isDuplicateOfBanner ? "AKTIV" : "LIVE"}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>
                          {isLive ? (
                            <>{stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"} · {fmtCurrency(stats.total)} · {fmtSince(s.starts_at)}</>
                          ) : noSales ? (
                            <>Keine Verkäufe · Dauer {fmtDuration(s.starts_at, s.ends_at)}</>
                          ) : (
                            <>{stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"} · {fmtCurrency(stats.total)} · Dauer {fmtDuration(s.starts_at, s.ends_at)}</>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 22, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>
                    </button>
                  );

                  return (
                    <SwipeToDelete
                      key={s.id}
                      id={`flea-session-${s.id}`}
                      tracker={swipeTracker}
                      threshold={80}
                      disabled={isLive}
                      disabledMessage="Markt läuft. Bitte erst beenden."
                      confirmTitle={`Markt-Tag vom ${fmtDateShort(s.starts_at)}${s.location ? ` · ${s.location}` : ""} wirklich löschen?`}
                      confirmSubtitle={stats.count > 0
                        ? `${stats.count} ${stats.count === 1 ? "Verkauf" : "Verkäufe"} werden permanent gelöscht.`
                        : "Der Markt-Tag wird permanent gelöscht."}
                      confirmWarning="Diese Aktion kann nicht rückgängig gemacht werden."
                      onConfirm={async () => { await deleteSession(s.id); }}
                      showToast={showToast}
                      t={t} dark={dark} F={F}
                    >
                      {cardContent}
                    </SwipeToDelete>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {startSheetOpen && (
        <FleaSessionStartSheet
          store={store} authToken={authToken} dark={dark} t={t}
          items={items}
          lastSession={sessions[0]}
          recentLocations={[...new Set(sessions.slice(0, 30).map(s => s.location).filter(Boolean))].slice(0, 5)}
          onClose={() => setStartSheetOpen(false)}
          onStarted={(newSession) => {
            setStartSheetOpen(false);
            onSessionStarted(newSession);
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaInventoryList — Tab "Inventar"
// ─────────────────────────────────────────────────────────────
function FleaInventoryList({ store, authToken, dark, t, items, categories: storeCategories, onCategoriesChanged, onItemsChanged, showToast }) {
  const [editing, setEditing] = useStateF(null); // null | { ...item } für edit | "new" für anlegen
  const [confirmDelete, setConfirmDelete] = useStateF(null);
  // Sort & Filter UI-State (nicht persistiert; Default = neueste zuerst)
  const [sortMode, setSortMode] = useStateF("newest"); // 'newest' | 'name' | 'price_asc' | 'price_desc'
  const [filterCategory, setFilterCategory] = useStateF(null); // null = alle | string = bestimmte Kategorie | '__none__' = ohne
  const [filterText, setFilterText] = useStateF("");

  // Kategorien-Liste — Migration 010: kommt jetzt aus dedizierter Tabelle.
  // Fallback (z.B. wenn Worker-Endpoint noch nicht da): aus Items extrahieren.
  const categories = useMemoF(() => {
    if (Array.isArray(storeCategories) && storeCategories.length > 0) {
      // Default-Kats vorne (sort_order), dann Custom alphabetisch
      return [...storeCategories]
        .sort((a, b) => {
          if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
          if (a.is_default && b.is_default) return (a.sort_order || 0) - (b.sort_order || 0);
          return (a.name || "").localeCompare(b.name || "", "de");
        })
        .map(c => c.name);
    }
    // Fallback aus Items
    const counts = new Map();
    for (const it of items) {
      const c = it.metadata?.category?.trim();
      if (c) counts.set(c, (counts.get(c) || 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [storeCategories, items]);

  const hasUncategorized = useMemoF(
    () => items.some(it => !it.metadata?.category?.trim()),
    [items]
  );

  const sortedItems = useMemoF(() => {
    const f = filterText.trim().toLowerCase();
    let list = items.filter(it => {
      // Text-Filter
      if (f && !it.name.toLowerCase().includes(f) && !(it.description || "").toLowerCase().includes(f)) return false;
      // Kategorie-Filter
      const cat = it.metadata?.category?.trim() || null;
      if (filterCategory === "__none__") return !cat;
      if (filterCategory) return cat === filterCategory;
      return true;
    });
    const cmp = {
      newest:     (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      name:       (a, b) => (a.name || "").localeCompare(b.name || "", "de"),
      price_asc:  (a, b) => (Number(a.wish_price) || 0) - (Number(b.wish_price) || 0),
      price_desc: (a, b) => (Number(b.wish_price) || 0) - (Number(a.wish_price) || 0),
    }[sortMode] || ((a, b) => 0);
    return [...list].sort(cmp);
  }, [items, sortMode, filterCategory, filterText]);

  const persistItem = async (data, idForUpdate) => {
    const apiBase = `/api/stores/${store.id}`;
    try {
      if (idForUpdate) {
        const updated = await apiCall(authToken, `${apiBase}/items/${idForUpdate}`, { method: "PATCH", body: data });
        onItemsChanged(items.map(it => it.id === idForUpdate ? updated : it));
        showToast("Item aktualisiert");
      } else {
        const created = await apiCall(authToken, `${apiBase}/items`, { method: "POST", body: data });
        onItemsChanged([created, ...items]);
        showToast("Item angelegt");
      }
      haptic(15);
    } catch (e) {
      showToast(e.message || "Speichern fehlgeschlagen", "error");
    }
  };

  const softDeleteItem = async (id) => {
    const apiBase = `/api/stores/${store.id}`;
    try {
      await apiCall(authToken, `${apiBase}/items/${id}`, { method: "DELETE" });
      onItemsChanged(items.filter(it => it.id !== id));
      showToast("Item gelöscht");
      haptic(15);
    } catch (e) {
      showToast(e.message || "Löschen fehlgeschlagen", "error");
    }
  };

  return (
    <>
      <button onClick={() => { setEditing("new"); haptic(10); }} style={{
        width: "100%", padding: "14px",
        background: t.accent.purple.solid,
        border: "none", borderRadius: 14,
        color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 15,
        cursor: "pointer", marginBottom: 16,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        boxShadow: `0 4px 18px ${t.accent.purple.weak}`,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Neues Item
      </button>

      {/* Sort & Kategorie-Filter — nur wenn überhaupt Items existieren */}
      {items.length > 0 && (
        <>
          {/* Such-/Filter-Input (sichtbar ab 5+ Items) */}
          {items.length > 5 && (
            <div style={{
              marginBottom: 10,
              background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
              borderRadius: 10, padding: "0 12px",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: t.textMuted, flexShrink: 0 }}>
                <circle cx="6" cy="6" r="4"/><path d="m12 12-3-3" strokeLinecap="round"/>
              </svg>
              <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Suchen …"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "10px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
              {filterText && (
                <button onClick={() => setFilterText("")} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          )}

          {/* Kategorie-Pills (horizontal scrollbar wenn lang) */}
          {(categories.length > 0 || hasUncategorized) && (
            <div style={{
              display: "flex", gap: 6, marginBottom: 10,
              overflowX: "auto", WebkitOverflowScrolling: "touch",
              paddingBottom: 4, scrollbarWidth: "none",
            }}>
              {[
                { id: null, label: `Alle · ${items.length}` },
                ...categories.map(c => ({ id: c, label: `${c} · ${items.filter(it => it.metadata?.category === c).length}` })),
                ...(hasUncategorized ? [{ id: "__none__", label: `Ohne Kategorie` }] : []),
              ].map(opt => {
                const active = filterCategory === opt.id;
                return (
                  <button key={String(opt.id)} onClick={() => { setFilterCategory(opt.id); haptic(6); }} style={{
                    flexShrink: 0,
                    background: active ? t.accent.purple.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                    color: active ? "#fff" : t.text,
                    border: `1px solid ${active ? t.accent.purple.solid : t.glassBorder}`,
                    borderRadius: 18, padding: "6px 12px",
                    fontFamily: F, fontWeight: active ? 700 : 500, fontSize: 12,
                    cursor: "pointer", whiteSpace: "nowrap",
                    transition: "all 0.15s ease",
                  }}>{opt.label}</button>
                );
              })}
            </div>
          )}

          {/* Sort-Auswahl */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 10, paddingLeft: 4,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              {sortedItems.length} {sortedItems.length === 1 ? "Item" : "Items"}
            </div>
            <select value={sortMode} onChange={e => setSortMode(e.target.value)} style={{
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              color: t.text, border: `1px solid ${t.glassBorder}`,
              borderRadius: 8, padding: "5px 10px",
              fontFamily: F, fontSize: 12, fontWeight: 600,
              cursor: "pointer", outline: "none",
            }}>
              <option value="newest">Neueste zuerst</option>
              <option value="name">Name A–Z</option>
              <option value="price_desc">Preis ↓ (teuer zuerst)</option>
              <option value="price_asc">Preis ↑ (günstig zuerst)</option>
            </select>
          </div>
        </>
      )}

      {items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.textSub, fontSize: 14, lineHeight: 1.5 }}>
          {/* P-Polish 1.14: freundlicherer Empty-State */}
          <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: t.text, marginBottom: 4 }}>
            Inventar ist leer
          </div>
          <div style={{ fontSize: 12 }}>
            Lege dein erstes Item an um zu starten.
          </div>
        </div>
      ) : sortedItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "30px 20px", color: t.textSub, fontSize: 14 }}>
          Keine Treffer mit aktuellem Filter.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sortedItems.map(it => {
            const cat = it.metadata?.category?.trim();
            return (
              <button key={it.id} onClick={() => setEditing(it)} style={{
                width: "100%",
                background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
                border: `1px solid ${t.glassBorder}`,
                borderRadius: 12, padding: "12px 14px",
                cursor: "pointer", fontFamily: F, textAlign: "left",
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: t.accent.purple.weak, color: t.accent.purple.solid,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {Icon.tag(20)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{it.name}</div>
                    {cat && (
                      <span style={{
                        fontSize: 10, fontWeight: 600,
                        background: t.accent.purple.weak, color: t.accent.purple.solid,
                        padding: "2px 7px", borderRadius: 5, flexShrink: 0,
                        whiteSpace: "nowrap", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis",
                      }}>{cat}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: t.textSub, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>
                    {/* P-Polish 1.11: explizit "Wunsch:" Prefix */}
                    Wunsch: {fmtCurrency(it.wish_price)}
                    {it.description && <span style={{ marginLeft: 6 }}>· {String(it.description).slice(0, 40)}</span>}
                  </div>
                </div>
                <div style={{ fontSize: 18, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>
              </button>
            );
          })}
        </div>
      )}

      {editing && (
        <FleaItemEditSheet
          item={editing === "new" ? null : editing}
          knownCategories={categories}
          store={store}
          authToken={authToken}
          storeCategoryRows={storeCategories || []}
          onCategoryAdded={(newCat) => {
            // Optimistisch lokal anhängen + Parent-Aware
            if (onCategoriesChanged) onCategoriesChanged([...(storeCategories || []), newCat]);
          }}
          dark={dark} t={t}
          onClose={() => setEditing(null)}
          onSave={async (data) => { await persistItem(data, editing === "new" ? null : editing.id); setEditing(null); }}
          onDelete={editing !== "new" ? () => { setConfirmDelete(editing); } : null}
          showToast={showToast}
        />
      )}

      {confirmDelete && (
        <ConfirmSheet
          dark={dark} t={t}
          title="Item löschen?"
          subtitle={confirmDelete.name}
          warning="Das Item wird inaktiv gesetzt. Bisherige Verkäufe bleiben in der Historie."
          confirmLabel="Löschen"
          confirmDestructive={true}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => { await softDeleteItem(confirmDelete.id); setConfirmDelete(null); setEditing(null); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaItemEditSheet — Item anlegen/bearbeiten
// ─────────────────────────────────────────────────────────────
function FleaItemEditSheet({ item, knownCategories, store, authToken, storeCategoryRows, onCategoryAdded, dark, t, onClose, onSave, onDelete, showToast }) {
  const [name, setName] = useStateF(item?.name || "");
  const [wishPrice, setWishPrice] = useStateF(item?.wish_price != null ? String(item.wish_price) : "");
  const [description, setDescription] = useStateF(item?.description || "");
  const [category, setCategory] = useStateF(item?.metadata?.category || "");
  const [dropdownOpen, setDropdownOpen] = useStateF(false);
  const [addCategoryOpen, setAddCategoryOpen] = useStateF(false);
  const [submitting, setSubmitting] = useStateF(false);

  const trimmedName = name.trim();
  const trimmedCat = category.trim();
  const priceNum = parseFloat(String(wishPrice).replace(",", "."));

  const { validate, errors, clearError, registerRef } = useValidation({
    name:      { required: true },
    wishPrice: { required: true, type: "price_gte0" },
  });

  const submit = async () => {
    if (submitting) return;
    if (!validate({ name, wishPrice })) return;
    setSubmitting(true);
    // Metadata mergen: bestehende Felder bewahren, category aktualisieren
    const newMetadata = { ...(item?.metadata || {}) };
    if (trimmedCat) newMetadata.category = trimmedCat;
    else delete newMetadata.category;
    await onSave({
      name: trimmedName,
      wish_price: priceNum,
      description: description.trim() || null,
      metadata: newMetadata,
      // image_key kommt in v2
    });
    setSubmitting(false);
  };

  const catPickerFooter = (
    <button type="button" onClick={() => { setDropdownOpen(false); setAddCategoryOpen(true); haptic(8); }} style={{
      width: "100%", padding: "13px",
      background: "transparent", border: `1px solid ${t.glassBorder}`,
      borderRadius: 12, color: t.accent.purple.solid,
      fontFamily: F, fontWeight: 600, fontSize: 14, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    }}>
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <path d="M7 2v10M2 7h10"/>
      </svg>
      Eigene Kategorie hinzufügen
    </button>
  );

  const submitFooter = (
    <div>
      <button onClick={submit} disabled={submitting} style={{
        width: "100%", padding: "14px",
        background: t.accent.purple.solid,
        color: "#fff",
        border: "none", borderRadius: 14,
        fontFamily: F, fontWeight: 700, fontSize: 15,
        cursor: submitting ? "default" : "pointer",
        boxShadow: `0 6px 20px ${t.accent.purple.weak}`,
        opacity: submitting ? 0.7 : 1,
        transition: "opacity 0.18s ease",
      }}>{item ? "Speichern" : "Anlegen"}</button>
      {onDelete && (
        <button onClick={onDelete} style={{
          width: "100%", marginTop: 8, padding: "10px",
          background: "transparent", border: "none",
          color: t.accent.red.solid,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Item löschen</button>
      )}
    </div>
  );

  return (
    <>
      <BottomSheet
        isOpen={true} onClose={onClose}
        size="medium" title={item ? "Item bearbeiten" : "Neues Item"}
        dark={dark} t={t} F={F}
        stickyFooter={submitFooter}
      >
        <div style={{ padding: "4px 22px 0" }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Name <span style={{ color: t.accent.red.solid }}>*</span>
          </div>
          <div ref={registerRef("name")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: errors.name ? 4 : 14, border: `1px solid ${errors.name ? "rgba(255,59,48,0.6)" : t.glassBorder}` }}>
            <input type="text" value={name} onChange={e => { setName(e.target.value); clearError("name"); }}
              placeholder="z.B. Vinyl Beatles, Vase, Buch …"
              style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 15, color: t.text, fontFamily: F, minWidth: 0 }}/>
          </div>
          {errors.name && <div style={{ color: "#ff3b30", fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{errors.name}</div>}

          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Wunschpreis € <span style={{ color: t.accent.red.solid }}>*</span>
          </div>
          <div ref={registerRef("wishPrice")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: errors.wishPrice ? 4 : 14, border: `1px solid ${errors.wishPrice ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={wishPrice} onChange={e => { setWishPrice(e.target.value); clearError("wishPrice"); }}
              placeholder="0,00"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 17, fontWeight: 600, color: t.text, fontFamily: F, minWidth: 0 }}/>
            <span style={{ color: t.textMuted, fontSize: 15 }}>€</span>
          </div>
          {errors.wishPrice && <div style={{ color: "#ff3b30", fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{errors.wishPrice}</div>}

          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Kategorie
          </div>
          <button type="button" onClick={() => setDropdownOpen(true)} style={{
            width: "100%", marginBottom: 14,
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            borderRadius: 12, padding: "12px 14px",
            border: `1px solid ${t.glassBorder}`,
            cursor: "pointer", fontFamily: F,
            display: "flex", alignItems: "center", gap: 8,
            color: trimmedCat ? t.text : t.textMuted, fontSize: 14, fontWeight: 500,
            textAlign: "left",
          }}>
            <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {trimmedCat || "Wähle Kategorie"}
            </span>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ color: t.textMuted, flexShrink: 0 }}>
              <path d="M2 4L6 8L10 4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Notiz
          </div>
          <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}` }}>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Zustand, Herkunft, …"
              rows={3}
              style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 14, color: t.text, fontFamily: F, resize: "none", minWidth: 0 }}/>
          </div>
        </div>
      </BottomSheet>

      {/* Kategorie-Picker als eigenes overlaying BottomSheet */}
      <BottomSheet
        isOpen={dropdownOpen} onClose={() => setDropdownOpen(false)}
        size="medium" title="Kategorie wählen"
        dark={dark} t={t} F={F}
        stickyFooter={catPickerFooter}
      >
        <div style={{ padding: "4px 16px 0" }}>
          {knownCategories.length === 0 && (
            <div style={{ padding: "16px 4px", fontSize: 13, color: t.textMuted, fontStyle: "italic" }}>
              Noch keine Kategorien angelegt.
            </div>
          )}
          {knownCategories.map(c => (
            <button key={c} type="button" onClick={() => { setCategory(c); setDropdownOpen(false); haptic(6); }} style={{
              width: "100%", padding: "13px 12px",
              background: category === c ? t.accent.purple.weak : "transparent",
              border: "none", borderBottom: `0.5px solid ${t.divider}`,
              color: category === c ? t.accent.purple.solid : t.text,
              fontFamily: F, fontWeight: category === c ? 600 : 500, fontSize: 15,
              textAlign: "left", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c}</span>
              {category === c && (
                <svg width="16" height="16" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: t.accent.purple.solid }}>
                  <polyline points="2 7 6 11 12 3"/>
                </svg>
              )}
            </button>
          ))}
          {trimmedCat && (
            <button type="button" onClick={() => { setCategory(""); setDropdownOpen(false); haptic(6); }} style={{
              width: "100%", padding: "13px 12px",
              background: "transparent", border: "none",
              color: t.textMuted, fontFamily: F, fontSize: 13, fontStyle: "italic",
              textAlign: "left", cursor: "pointer",
            }}>Keine Kategorie</button>
          )}
        </div>
      </BottomSheet>

      {addCategoryOpen && (
        <FleaCategoryAddSheet
          store={store} authToken={authToken}
          dark={dark} t={t}
          onClose={() => setAddCategoryOpen(false)}
          onAdded={(newCat) => {
            setAddCategoryOpen(false);
            setCategory(newCat.name);
            if (onCategoryAdded) onCategoryAdded(newCat);
          }}
          showToast={showToast}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaCategoryAddSheet — neue Custom-Kategorie schnell anlegen
// (geöffnet aus FleaItemEditSheet "+ Eigene Kategorie hinzufügen")
// ─────────────────────────────────────────────────────────────
function FleaCategoryAddSheet({ store, authToken, dark, t, onClose, onAdded, showToast }) {
  const [name, setName] = useStateF("");
  const [submitting, setSubmitting] = useStateF(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const trimmed = name.trim();
  const { validate: validateCat, errors: catErrors, clearError: clearCatError, registerRef: registerCatRef } = useValidation({
    name: { required: true },
  });
  const submit = async () => {
    if (submitting) return;
    if (!validateCat({ name })) return;
    setSubmitting(true);
    try {
      const newCat = await apiCall(authToken, `/api/stores/${store.id}/flea-categories`, {
        method: "POST", body: { name: trimmed },
      });
      haptic(15);
      showToast("Kategorie angelegt");
      onAdded(newCat);
    } catch (e) {
      // Worker liefert 409 wenn Kategorie schon existiert
      const msg = e.message?.includes("409") ? "Diese Kategorie existiert bereits" : (e.message || "Anlegen fehlgeschlagen");
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 96,
        background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.4)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 97 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Neue Kategorie" onClose={onClose} marginBottom={14} />
        <div ref={registerCatRef("name")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: catErrors.name ? 4 : 14, border: `1px solid ${catErrors.name ? "rgba(255,59,48,0.6)" : t.glassBorder}` }}>
          <input type="text" value={name} onChange={e => { setName(e.target.value); clearCatError("name"); }}
            placeholder="z.B. Vintage, Comics, Werkzeug …"
            onKeyDown={e => { if (e.key === "Enter") submit(); }}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 15, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        {catErrors.name && <div style={{ color: "#ff3b30", fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{catErrors.name}</div>}
        <button onClick={submit} disabled={submitting} style={{
          width: "100%", padding: "13px",
          background: t.accent.purple.solid,
          color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.7 : 1,
        }}>{submitting ? "Lege an…" : "Anlegen"}</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSessionStartSheet — Markt-Tag setup
// ─────────────────────────────────────────────────────────────
function FleaSessionStartSheet({ store, authToken, dark, t, items, lastSession, recentLocations, onClose, onStarted, showToast }) {
  const [location, setLocation] = useStateF("");
  const [selectedIds, setSelectedIds] = useStateF(new Set());
  const [submitting, setSubmitting] = useStateF(false);

  const activeItems = useMemoF(() =>
    [...items].filter(it => it.active !== false).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
  , [items]);

  const toggleItem = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
    haptic(6);
  };
  const selectAll  = () => { setSelectedIds(new Set(activeItems.map(it => it.id))); haptic(10); };
  const selectLast = () => {
    if (!lastSession?.metadata?.selected_item_ids) return;
    const lastIds = new Set(lastSession.metadata.selected_item_ids);
    // Schnittmenge mit aktiven Items (manche könnten gelöscht sein)
    const valid = activeItems.filter(it => lastIds.has(it.id)).map(it => it.id);
    setSelectedIds(new Set(valid));
    haptic(10);
  };
  const clearAll   = () => { setSelectedIds(new Set()); haptic(6); };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = {
        location: location.trim() || null,
        starts_at: new Date().toISOString(),
        metadata: { selected_item_ids: [...selectedIds] },
      };
      const session = await apiCall(authToken, `/api/stores/${store.id}/sessions`, { method: "POST", body });
      haptic(20);
      onStarted(session);
    } catch (e) {
      showToast(e.message || "Markt konnte nicht gestartet werden", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const startFooter = (
    <button onClick={submit} disabled={submitting} style={{
      width: "100%", padding: "14px",
      background: "linear-gradient(180deg, #BF5AF2 0%, #9F3DD2 100%)",
      border: "none", borderRadius: 14,
      color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16,
      cursor: submitting ? "default" : "pointer",
      opacity: submitting ? 0.6 : 1,
      boxShadow: "0 6px 22px rgba(191,90,242,0.4)",
    }}>{submitting ? "Starte…" : "Markt starten"}</button>
  );

  return (
    <BottomSheet
      isOpen={true} onClose={onClose}
      size="medium" title="Markt-Tag starten"
      dark={dark} t={t} F={F}
      stickyFooter={startFooter}
    >
      <div style={{ padding: "4px 22px 0" }}>
        {/* Ort */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>Ort</div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}` }}>
          <input type="text" value={location} onChange={e => setLocation(e.target.value)}
            placeholder="z.B. Flohmarkt Tempelhof"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 15, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        {recentLocations.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {recentLocations.map(loc => (
              <button key={loc} onClick={() => setLocation(loc)} style={{
                background: t.accent.blue.weak, color: t.accent.blue.solid,
                border: "none", borderRadius: 8, padding: "5px 10px",
                fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: F,
              }}>{loc}</button>
            ))}
          </div>
        )}

        {/* Inventar-Auswahl */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", paddingLeft: 4 }}>
            Items dabei{selectedIds.size > 0 ? ` · ${selectedIds.size}` : ""}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {lastSession?.metadata?.selected_item_ids?.length > 0 && (
              <button onClick={selectLast} style={{ fontSize: 11, fontWeight: 600, color: t.accent.blue.solid, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", fontFamily: F }}>Wie letztes Mal</button>
            )}
            {selectedIds.size === 0 ? (
              <button onClick={selectAll} style={{ fontSize: 11, fontWeight: 600, color: t.accent.blue.solid, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", fontFamily: F }}>Alle</button>
            ) : (
              <button onClick={clearAll} style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", fontFamily: F }}>Keine</button>
            )}
          </div>
        </div>

        {activeItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 13 }}>
            Noch keine Items im Inventar.<br/>Du kannst trotzdem starten und Spontan-Verkäufe machen.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 8 }}>
            {activeItems.map(it => {
              const sel = selectedIds.has(it.id);
              return (
                <button key={it.id} onClick={() => toggleItem(it.id)} style={{
                  width: "100%",
                  background: sel ? t.accent.purple.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                  border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                  borderRadius: 11, padding: "10px 12px",
                  cursor: "pointer", fontFamily: F, textAlign: "left",
                  display: "flex", alignItems: "center", gap: 10,
                  transition: "all 0.14s ease",
                }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                    background: sel ? t.accent.purple.solid : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {sel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: sel ? 600 : 500, color: sel ? t.accent.purple.solid : t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                  <span style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(it.wish_price)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaActiveSession — Hauptbildschirm während Markt läuft
// ─────────────────────────────────────────────────────────────
function FleaActiveSession({ store, authToken, dark, t, session, items, onSessionChanged, onItemsChanged, showToast, onBackToLanding, onShowHub }) {
  const [sales, setSales] = useStateF([]);
  const [filter, setFilter] = useStateF("");
  const [saleSheetItem, setSaleSheetItem] = useStateF(null);    // Item das gerade verkauft wird
  const [stornoSale, setStornoSale] = useStateF(null);
  const [bundleOpen, setBundleOpen] = useStateF(false);
  const [spontaneousOpen, setSpontaneousOpen] = useStateF(false);
  const [endSheetOpen, setEndSheetOpen] = useStateF(false);
  const [addItemsOpen, setAddItemsOpen] = useStateF(false);    // Items mid-session hinzunehmen
  // P-Polish 1.6 + 1.12: Drei-Punkte-Menü oben rechts (vorher gab's keins).
  const [actionMenuOpen, setActionMenuOpen] = useStateF(false);
  const [loadingSales, setLoadingSales] = useStateF(true);
  // Live-Updates der "seit X" Anzeige
  const [now, setNow] = useStateF(Date.now());
  useEffectF(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  // P-5-Punkte-Fix Block 5: Auto-Refresh-Indikator + Member-Count
  const [memberCount, setMemberCount] = useStateF(1);
  const [refreshPulse, setRefreshPulse] = useStateF(false);

  const apiBase = `/api/stores/${store.id}`;

  // Reload-Helper für Sales (used by initial-load, PTR, auto-refresh)
  const reloadSales = async (silentToast = true) => {
    try {
      const detail = await apiCall(authToken, `${apiBase}/sessions/${session.id}`);
      const newSales = detail?.sales || [];
      const oldCount = sales.filter(s => !s.is_voided).length;
      const newCount = newSales.filter(s => !s.is_voided).length;
      setSales(newSales);
      // Pulse-Indicator
      setRefreshPulse(true);
      setTimeout(() => setRefreshPulse(false), 1200);
      // Bei neuen Verkäufen sanftes Toast (nur wenn nicht silent)
      if (!silentToast && newCount > oldCount) {
        showToast(`${newCount - oldCount} neue${newCount - oldCount === 1 ? "r" : ""} Verkauf`);
      }
    } catch (e) {
      if (!silentToast) showToast("Aktualisieren fehlgeschlagen", "error");
    }
  };

  // Sales dieser Session laden (initial mit nested sale_items)
  useEffectF(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiCall(authToken, `${apiBase}/sessions/${session.id}`);
        if (!cancelled) setSales(detail?.sales || []);
      } catch (e) {
        if (!cancelled) showToast("Verkäufe konnten nicht geladen werden", "error");
      } finally {
        if (!cancelled) setLoadingSales(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  // P-5-Punkte-Fix Block 5: Member-Count laden für Auto-Refresh-Decision
  useEffectF(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/members`, {
          headers: { "Authorization": `Bearer ${authToken}` },
        });
        if (!r.ok) return;
        const members = await r.json();
        if (!cancelled) setMemberCount(members.length || 1);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [store.id]);

  // P-5-Punkte-Fix Block 5: Auto-Refresh alle 30s wenn ≥2 Mitglieder.
  // Bei 1 Mitglied (= Solo-User) kein Polling — User sieht eh seine Daten.
  useEffectF(() => {
    if (memberCount < 2) return;
    const interval = setInterval(() => {
      reloadSales(true /* silent toast */);
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [memberCount, session.id]);

  // selected_item_ids aus Session-metadata
  const selectedItemIds = useMemoF(() =>
    new Set(session.metadata?.selected_item_ids || [])
  , [session.metadata]);

  // Items die in dieser Session verkauft sind (non-voided)
  const soldItemIds = useMemoF(() => {
    const ids = new Set();
    for (const sale of sales) {
      if (sale.is_voided) continue;
      for (const si of (sale.sale_items || [])) {
        if (si.item_id) ids.add(si.item_id);
      }
    }
    return ids;
  }, [sales]);

  // Available = aus selected_item_ids minus verkaufte, plus Filter
  const availableItems = useMemoF(() => {
    const f = filter.trim().toLowerCase();
    return items
      .filter(it => selectedItemIds.has(it.id) && !soldItemIds.has(it.id) && it.active !== false)
      .filter(it => !f || it.name.toLowerCase().includes(f) || (it.description || "").toLowerCase().includes(f))
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [items, selectedItemIds, soldItemIds, filter]);

  // Sold-Liste (alle non-voided Sales, sortiert neueste zuerst)
  const soldList = useMemoF(() =>
    [...sales].sort((a, b) => (b.sold_at || "").localeCompare(a.sold_at || ""))
  , [sales]);

  const stats = computeSessionStats(sales);

  // Sync-Event-Listener — lädt Sales neu wenn Background-Retry für diese Session erfolgreich war
  useEffectF(() => {
    const handler = () => reloadSales(true);
    window.addEventListener("memorica:sale_synced", handler);
    return () => window.removeEventListener("memorica:sale_synced", handler);
  // eslint-disable-next-line
  }, []);

  // Handler — Verkauf abschließen (optimistisch + SyncManager)
  const completeSale = async (body) => {
    const sm = syncManager;
    const localId = "pending_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
    // Optimistisches State-Update
    const optimistic = {
      id: localId,
      total: body.total,
      payment: body.payment,
      is_bundle: body.is_bundle || false,
      is_voided: false,
      sold_at: new Date().toISOString(),
      sale_items: (body.items || []).map(i => ({
        item_id: i.item_id || null,
        item_name: i.item_name || "",
        unit_price: i.unit_price || 0,
        wish_price: i.wish_price || 0,
        quantity: i.quantity || 1,
      })),
      _pending: true,
    };
    setSales(prev => [optimistic, ...prev]);
    haptic(20);

    if (!sm) {
      // Kein SyncManager → direkter API-Call
      try {
        const sale = await apiCall(authToken, `${apiBase}/sessions/${session.id}/sales`, { method: "POST", body });
        setSales(prev => prev.map(s => s.id === localId ? sale : s));
        showToast(`Verkauft · ${fmtCurrency(sale.total)}`);
      } catch (e) {
        setSales(prev => prev.filter(s => s.id !== localId));
        showToast(e.message || "Verkauf fehlgeschlagen", "error");
      }
      return;
    }

    const result = await sm.write({
      authToken,
      endpoint: `${apiBase}/sessions/${session.id}/sales`,
      method: "POST",
      payload: body,
      action_type: "create_sale",
      store_id: store.id,
    });

    if (result.ok) {
      setSales(prev => prev.map(s => s.id === localId ? result.data : s));
      showToast(`Verkauft · ${fmtCurrency(result.data.total)}`);
    } else if (result.queued) {
      showToast("Offline – Verkauf gespeichert", "warning");
    } else {
      setSales(prev => prev.filter(s => s.id !== localId));
      showToast(result.error || "Verkauf fehlgeschlagen", "error");
    }
  };

  // Handler — Storno (optimistisch + SyncManager)
  const stornoSubmit = async (sale) => {
    // Pending-Sales haben keine echte Server-ID — kein Storno möglich
    if (sale._pending) { showToast("Verkauf wird noch synchronisiert", "warning"); return; }

    const sm = syncManager;
    setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: true, _pending: true } : s));
    haptic(15);

    if (!sm) {
      try {
        const updated = await apiCall(authToken, `${apiBase}/sales/${sale.id}`, { method: "PATCH", body: { is_voided: true } });
        setSales(prev => prev.map(s => s.id === sale.id ? { ...s, ...updated, sale_items: s.sale_items, _pending: false } : s));
        showToast("Verkauf storniert");
      } catch (e) {
        setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: false, _pending: false } : s));
        showToast(e.message || "Storno fehlgeschlagen", "error");
      }
      return;
    }

    const result = await sm.write({
      authToken,
      endpoint: `${apiBase}/sales/${sale.id}`,
      method: "PATCH",
      payload: { is_voided: true },
      action_type: "void_sale",
      store_id: store.id,
    });

    if (result.ok) {
      setSales(prev => prev.map(s => s.id === sale.id ? { ...s, ...result.data, sale_items: s.sale_items, _pending: false } : s));
      showToast("Verkauf storniert");
    } else if (result.queued) {
      showToast("Offline – Storno gespeichert", "warning");
    } else {
      setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: false, _pending: false } : s));
      showToast(result.error || "Storno fehlgeschlagen", "error");
    }
  };

  // Handler — Items mid-Session in die laufende Session aufnehmen.
  // newIds = Array von Item-IDs die zusätzlich zum bestehenden selected_item_ids
  // dazukommen sollen. Sendet PATCH /sessions/:id mit gemergter Metadata.
  const addItemsToActiveSession = async (newIds) => {
    if (!newIds || newIds.length === 0) return;
    try {
      const merged = [...new Set([...(session.metadata?.selected_item_ids || []), ...newIds])];
      const updated = await apiCall(authToken, `${apiBase}/sessions/${session.id}`, {
        method: "PATCH",
        body: { metadata: { ...(session.metadata || {}), selected_item_ids: merged } },
      });
      onSessionChanged(updated);
      haptic(15);
      showToast(`${newIds.length} ${newIds.length === 1 ? "Item dabei" : "Items dabei"}`);
    } catch (e) {
      showToast(e.message || "Hinzufügen fehlgeschlagen", "error");
    }
  };

  const accent = t.accent.purple;

  return (
    <div style={{
      // height (nicht minHeight) damit der innere Scroll-Container bounded ist.
      height: "100dvh", maxHeight: "100dvh",
      background: dark ? "#0d1117" : "#dde8f8", fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* P-Polish 1.12: Back-Button geht zur Landing (nicht mehr zum Hub).
              Hub bleibt über das Drei-Punkte-Menü erreichbar. */}
          <button onClick={onBackToLanding} aria-label="Zurück" style={{
            display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
            height: 30, padding: "0 10px 0 8px", borderRadius: 15,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", fontFamily: F,
            fontSize: 12, fontWeight: 600,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Zurück
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent.solid, animation: "pulse 1.6s ease-in-out infinite" }}/>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {session.location || "Trödel-Markt"}
              </div>
            </div>
            <div style={{ fontSize: 11, color: t.textSub, marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
              {fmtSince(session.starts_at)}
              {/* P-5-Punkte-Fix Block 5: Auto-Refresh-Pulse-Indicator (subtil) */}
              {memberCount >= 2 && (
                <span aria-label="Sync aktiv" style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: refreshPulse ? t.accent.green.solid : t.textMuted,
                  opacity: refreshPulse ? 1 : 0.4,
                  transition: "all 0.4s ease",
                  marginLeft: 4,
                }}/>
              )}
            </div>
          </div>
          <SyncIndicator dark={dark} />
          {/* P-Polish 1.6 + 1.12: Drei-Punkte-Menü mit "Items hinzunehmen",
              "Hub anzeigen", "Markt beenden". */}
          <button onClick={() => { setActionMenuOpen(true); haptic(8); }} aria-label="Mehr" style={{
            width: 30, height: 30, borderRadius: 15, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Stats Hero */}
        <div style={{ padding: "20px 16px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: t.text, letterSpacing: "-1px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {fmtCurrency(stats.total)}
          </div>
          <div style={{ fontSize: 13, color: t.textSub, marginTop: 4 }}>
            {stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}
            {stats.bundleCount > 0 && ` · ${stats.bundleCount} Bundle`}
          </div>
          {(stats.cash > 0 || stats.card > 0) && (
            <div style={{ display: "inline-flex", gap: 12, marginTop: 8, fontSize: 12, color: t.textMuted }}>
              {stats.cash > 0 && <span>Bar {fmtCurrency(stats.cash)}</span>}
              {stats.card > 0 && <span>Karte {fmtCurrency(stats.card)}</span>}
            </div>
          )}
          {stats.wishSum > 0 && stats.count > 0 && (
            <div style={{
              fontSize: 11, color: stats.diff < 0 ? t.accent.orange.solid : t.accent.green.solid,
              marginTop: 6, fontWeight: 600,
            }}>
              {stats.diff >= 0 ? "+" : ""}{fmtCurrency(stats.diff)} ggü. Wunschpreis ({stats.diffPct >= 0 ? "+" : ""}{stats.diffPct}%)
            </div>
          )}
        </div>

        {/* Available */}
        <div style={{ padding: "12px 16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Verfügbar · {availableItems.length}
            </div>
          </div>
          {selectedItemIds.size > 5 && (
            <div style={{ marginBottom: 10, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: "0 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: t.textMuted, flexShrink: 0 }}>
                <circle cx="6" cy="6" r="4"/><path d="m12 12-3-3" strokeLinecap="round"/>
              </svg>
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtern …"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "10px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
              {filter && (
                <button onClick={() => setFilter("")} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          )}
          {availableItems.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: t.textMuted, fontSize: 13 }}>
              {filter ? "Nichts gefunden." : selectedItemIds.size === 0 ? "Keine Items mitgenommen. Nutze Spontan-Verkauf." : "Alles verkauft 🎉"}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {availableItems.map(it => (
                <button key={it.id} onClick={() => { setSaleSheetItem(it); haptic(8); }} style={{
                  width: "100%",
                  background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
                  border: `1px solid ${t.glassBorder}`,
                  borderRadius: 12, padding: "10px 12px",
                  cursor: "pointer", fontFamily: F, textAlign: "left",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                    background: t.accent.purple.weak, color: t.accent.purple.solid,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{Icon.tag(18)}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</div>
                    <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>Wunsch {fmtCurrency(it.wish_price)}</div>
                  </div>
                  <div style={{ fontSize: 16, color: t.chevron, fontWeight: 200 }}>›</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sold */}
        {soldList.length > 0 && (
          <div style={{ padding: "8px 16px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              Verkauft · {stats.count}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {soldList.map(sale => {
                const isVoided = sale.is_voided;
                const isPending = sale._pending;
                const items = sale.sale_items || [];
                const headline = sale.is_bundle
                  ? `Bundle (${items.length} Items)`
                  : (items[0]?.item_name || "Verkauf");
                const wishSum = items.reduce((s, i) => s + (Number(i.wish_price) || 0), 0);
                const diff = Number(sale.total) - wishSum;
                return (
                  <button key={sale.id} onClick={() => !isPending && setStornoSale(sale)} style={{
                    width: "100%",
                    background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.5)",
                    border: `1px solid ${isPending ? t.accent.orange.solid + "55" : t.glassBorder}`,
                    borderRadius: 12, padding: "10px 12px",
                    cursor: isPending ? "default" : "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10,
                    opacity: isVoided ? 0.5 : isPending ? 0.75 : 1,
                    textDecoration: isVoided ? "line-through" : "none",
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: isVoided ? t.accent.red.weak : isPending ? "rgba(255,159,10,0.15)" : t.accent.green.weak,
                      color: isVoided ? t.accent.red.solid : isPending ? "#ff9f0a" : t.accent.green.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isVoided ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                      ) : isPending ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ animation: "ptrSpin 1.2s linear infinite" }}>
                          <path d="M6 1.5A4.5 4.5 0 0 1 10.5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <path d="M6 10.5A4.5 4.5 0 0 1 1.5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {headline}
                      </div>
                      <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
                        {fmtTime(sale.sold_at)} · {sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}
                        {wishSum > 0 && diff !== 0 && !isVoided && (
                          <span style={{ marginLeft: 6, color: diff < 0 ? t.accent.orange.solid : t.accent.green.solid }}>
                            {diff > 0 ? "+" : ""}{fmtCurrency(diff)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums" }}>
                      {fmtCurrency(sale.total)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Action-Buttons (P-Polish 1.6: "Items hinzunehmen" wandert ins
            Drei-Punkte-Menü oben rechts; hier nur Bundle + Spontan) */}
        <div style={{ padding: "8px 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={() => { setBundleOpen(true); haptic(8); }} style={{
            width: "100%", padding: "13px",
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            border: `1px solid ${t.glassBorder}`, borderRadius: 12,
            color: t.text, fontFamily: F, fontWeight: 600, fontSize: 14,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
              <path d="M2 5L8 2L14 5V11L8 14L2 11V5Z"/><path d="M2 5L8 8M8 8L14 5M8 8V14"/>
            </svg>
            Bundle-Verkauf
          </button>
          <button onClick={() => { setSpontaneousOpen(true); haptic(8); }} style={{
            width: "100%", padding: "13px",
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            border: `1px solid ${t.glassBorder}`, borderRadius: 12,
            color: t.text, fontFamily: F, fontWeight: 600, fontSize: 14,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M9 1L3 9H7L7 15L13 7H9V1Z" strokeLinejoin="round"/>
            </svg>
            Spontan-Verkauf
          </button>
        </div>

        {/* Markt beenden */}
        <div style={{ padding: "8px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)" }}>
          <button onClick={() => { setEndSheetOpen(true); haptic(10); }} style={{
            width: "100%", padding: "14px",
            background: t.accent.red.weak,
            border: `1px solid ${t.accent.red.solid}40`, borderRadius: 12,
            color: t.accent.red.solid, fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: "pointer",
          }}>Markt beenden</button>
        </div>
      </div>

      {/* Sheets */}
      {saleSheetItem && (
        <FleaSaleSheet
          item={saleSheetItem} dark={dark} t={t}
          onClose={() => setSaleSheetItem(null)}
          onConfirm={async (data) => {
            await completeSale({
              total: data.total,
              payment: data.payment,
              items: [{
                item_id: saleSheetItem.id,
                item_name: saleSheetItem.name,
                unit_price: data.total,
                wish_price: saleSheetItem.wish_price,
                quantity: 1,
              }],
            });
            setSaleSheetItem(null);
          }}
          showToast={showToast}
        />
      )}
      {bundleOpen && (
        <FleaBundleSheet
          availableItems={availableItems} dark={dark} t={t}
          onClose={() => setBundleOpen(false)}
          onConfirm={async (data) => {
            await completeSale({
              total: data.total,
              payment: data.payment,
              is_bundle: true,
              items: data.items.map(it => ({
                item_id: it.id,
                item_name: it.name,
                unit_price: null, // Bundle-Aufteilung später nachtragbar
                wish_price: it.wish_price,
                quantity: 1,
              })),
            });
            setBundleOpen(false);
          }}
          showToast={showToast}
        />
      )}
      {spontaneousOpen && (
        <FleaSpontaneousSaleSheet
          dark={dark} t={t}
          recentNames={[...new Set(sales
            .filter(s => !s.is_voided)
            .flatMap(s => (s.sale_items || []).filter(i => !i.item_id).map(i => i.item_name))
          )].slice(0, 10)}
          onClose={() => setSpontaneousOpen(false)}
          onConfirm={async (data) => {
            await completeSale({
              total: data.total,
              payment: data.payment,
              items: [{
                item_id: null,
                item_name: data.name || "Spontan-Verkauf",
                unit_price: data.total,
                quantity: 1,
              }],
            });
            setSpontaneousOpen(false);
          }}
          showToast={showToast}
        />
      )}
      {stornoSale && (
        <FleaSaleStornoSheet
          sale={stornoSale} dark={dark} t={t}
          onClose={() => setStornoSale(null)}
          onConfirm={async () => {
            await stornoSubmit(stornoSale);
            setStornoSale(null);
          }}
        />
      )}
      {endSheetOpen && (
        <FleaSessionEndSheet
          store={store} authToken={authToken} dark={dark} t={t}
          session={session}
          sales={sales}
          items={items}
          selectedItemIds={selectedItemIds}
          soldItemIds={soldItemIds}
          onClose={() => setEndSheetOpen(false)}
          onEnded={(updatedSession, updatedItems) => {
            setEndSheetOpen(false);
            if (updatedItems) onItemsChanged(updatedItems);
            onSessionChanged(updatedSession);
          }}
          showToast={showToast}
        />
      )}
      {addItemsOpen && (
        <FleaAddItemsSheet
          store={store} authToken={authToken} dark={dark} t={t}
          items={items}
          selectedItemIds={selectedItemIds}
          onClose={() => setAddItemsOpen(false)}
          onAdd={async (ids) => {
            await addItemsToActiveSession(ids);
            setAddItemsOpen(false);
          }}
          onItemCreated={(newItem) => {
            onItemsChanged([newItem, ...items]);
          }}
          showToast={showToast}
        />
      )}

      {actionMenuOpen && (
        <ActionMenu
          dark={dark} t={t}
          onClose={() => setActionMenuOpen(false)}
          accentColor={t.accent.purple}
          actions={[
            {
              label: "Items hinzunehmen",
              icon: "add",
              onClick: () => { setActionMenuOpen(false); setAddItemsOpen(true); },
            },
            {
              label: "Übersicht (Inventar / Stats)",
              icon: "list",
              onClick: () => { setActionMenuOpen(false); onShowHub && onShowHub(); },
            },
            {
              label: "Markt beenden",
              icon: "stop",
              destructive: true,
              onClick: () => { setActionMenuOpen(false); setEndSheetOpen(true); },
            },
          ]}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSaleSheet — Einzel-Verkauf
// ─────────────────────────────────────────────────────────────
function FleaSaleSheet({ item, dark, t, onClose, onConfirm, showToast }) {
  const [priceStr, setPriceStr] = useStateF(item?.wish_price != null ? String(item.wish_price) : "");
  const [submitting, setSubmitting] = useStateF(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const { validate, errors, clearError, registerRef } = useValidation({
    priceStr: { required: true, type: "price" },
  });

  const priceNum = parseFloat(String(priceStr).replace(",", "."));
  const wish = Number(item?.wish_price) || 0;
  const priceValid = Number.isFinite(priceNum) && priceNum > 0;
  const diff = priceValid && wish > 0 ? priceNum - wish : null;
  const diffPct = priceValid && wish > 0 ? Math.round((diff / wish) * 100) : null;
  const diffColor = diff === null ? t.textMuted : diff < 0 ? t.accent.orange.solid : diff > 0 ? t.accent.green.solid : t.textMuted;

  const submit = async (payment) => {
    if (submitting) return;
    if (!validate({ priceStr })) return;
    if (payment === "card") {
      showToast("Karten-Zahlung kommt mit der nächsten App-Version (SumUp)", "info");
      return;
    }
    setSubmitting(true);
    await onConfirm({ total: priceNum, payment });
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Verkaufen" subtitle={item.name} onClose={onClose} marginBottom={16} />

        <div ref={registerRef("priceStr")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 14, padding: "14px 16px", marginBottom: errors.priceStr ? 4 : 6, border: `1px solid ${errors.priceStr ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 10 }}>
          <input type="number" inputMode="decimal" step="0.01" min="0" value={priceStr} onChange={e => { setPriceStr(e.target.value); clearError("priceStr"); }}
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 28, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
          <span style={{ color: t.textMuted, fontSize: 22, fontWeight: 600 }}>€</span>
        </div>
        {errors.priceStr && <div style={{ color: "#ff3b30", fontSize: 11, textAlign: "center", marginBottom: 4 }}>{errors.priceStr}</div>}

        <div style={{ textAlign: "center", marginBottom: 18, fontSize: 12, color: diffColor, fontWeight: 600, minHeight: 18 }}>
          {wish > 0 ? (
            diff === null ? `Wunsch ${fmtCurrency(wish)}` :
            diff === 0   ? `= Wunschpreis` :
                           `Wunsch ${fmtCurrency(wish)} · ${diff > 0 ? "+" : ""}${fmtCurrency(diff)} (${diff > 0 ? "+" : ""}${diffPct}%)`
          ) : null}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
          <button onClick={() => submit("cash")} disabled={submitting} style={{
            padding: "16px",
            background: t.accent.green.solid,
            color: "#fff",
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: submitting ? "default" : "pointer",
            boxShadow: "0 6px 20px rgba(48,209,88,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>Bar</button>
          <button onClick={() => submit("card")} disabled={submitting} style={{
            padding: "16px",
            background: t.accent.blue.solid,
            color: "#fff",
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: submitting ? "default" : "pointer",
            boxShadow: "0 6px 20px rgba(10,132,255,0.35)",
            opacity: 0.85,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>Karte</button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaBundleSheet — Bundle-Verkauf (mehrere Items zusammen)
// ─────────────────────────────────────────────────────────────
function FleaBundleSheet({ availableItems, dark, t, onClose, onConfirm, showToast }) {
  const [selected, setSelected] = useStateF(new Set());
  const [priceStr, setPriceStr] = useStateF("");
  const [submitting, setSubmitting] = useStateF(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    haptic(6);
  };

  const wishSum = useMemoF(() =>
    availableItems.filter(it => selected.has(it.id)).reduce((s, it) => s + (Number(it.wish_price) || 0), 0)
  , [availableItems, selected]);

  const { validate: validateBundle, errors: bundleErrors, clearError: clearBundleError, registerRef: registerBundleRef } = useValidation({
    priceStr: { required: true, type: "price_gte0" },
  });

  const priceNum = parseFloat(String(priceStr).replace(",", "."));
  const priceValid = Number.isFinite(priceNum) && priceNum >= 0;
  const diff = priceValid && selected.size >= 2 ? priceNum - wishSum : null;
  const diffPct = priceValid && wishSum > 0 ? Math.round((diff / wishSum) * 100) : null;
  const diffColor = diff === null ? t.textMuted : diff < 0 ? t.accent.orange.solid : diff > 0 ? t.accent.green.solid : t.textMuted;

  const submit = async (payment) => {
    if (submitting) return;
    if (selected.size < 2) { showToast("Mindestens 2 Items wählen", "warning"); return; }
    if (!validateBundle({ priceStr })) return;
    if (payment === "card") {
      showToast("Karten-Zahlung kommt mit der nächsten App-Version (SumUp)", "info");
      return;
    }
    setSubmitting(true);
    const items = availableItems.filter(it => selected.has(it.id));
    await onConfirm({ total: priceNum, payment, items });
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 460, radius: 24, padding: 0, zIndex: 93 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Bundle-Verkauf" subtitle={`${selected.size} ${selected.size === 1 ? "Item" : "Items"} · Wunsch ${fmtCurrency(wishSum)}`} onClose={onClose} marginBottom={12} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 22px", maxHeight: Math.max(180, vh - 360) }}>
          {availableItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 13 }}>Keine Items verfügbar.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 12 }}>
              {availableItems.map(it => {
                const sel = selected.has(it.id);
                return (
                  <button key={it.id} onClick={() => toggle(it.id)} style={{
                    width: "100%",
                    background: sel ? t.accent.purple.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                    border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                    borderRadius: 11, padding: "10px 12px",
                    cursor: "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                      background: sel ? t.accent.purple.solid : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {sel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: sel ? 600 : 500, color: sel ? t.accent.purple.solid : t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                    <span style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(it.wish_price)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 22px calc(env(safe-area-inset-bottom, 14px) + 14px)", borderTop: `0.5px solid ${t.divider}` }}>
          <div ref={registerBundleRef("priceStr")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: bundleErrors.priceStr ? 4 : 4, border: `1px solid ${bundleErrors.priceStr ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={priceStr} onChange={e => { setPriceStr(e.target.value); clearBundleError("priceStr"); }}
              placeholder="Bundle-Preis"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 22, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
            <span style={{ color: t.textMuted, fontSize: 18, fontWeight: 600 }}>€</span>
          </div>
          {bundleErrors.priceStr && <div style={{ color: "#ff3b30", fontSize: 11, textAlign: "center", marginBottom: 4 }}>{bundleErrors.priceStr}</div>}
          <div style={{ textAlign: "center", marginBottom: 10, fontSize: 11, color: diffColor, fontWeight: 600, minHeight: 16 }}>
            {priceValid && selected.size >= 2 && wishSum > 0 && diff !== 0 && `${diff > 0 ? "+" : ""}${fmtCurrency(diff)} (${diff > 0 ? "+" : ""}${diffPct}%)`}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button onClick={() => submit("cash")} disabled={submitting} style={{
              padding: "14px",
              background: t.accent.green.solid,
              color: "#fff",
              border: "none", borderRadius: 12,
              fontFamily: F, fontWeight: 700, fontSize: 14,
              cursor: submitting ? "default" : "pointer",
            }}>Bar</button>
            <button onClick={() => submit("card")} disabled={submitting} style={{
              padding: "14px",
              background: t.accent.blue.solid,
              color: "#fff",
              border: "none", borderRadius: 12,
              fontFamily: F, fontWeight: 700, fontSize: 14,
              cursor: submitting ? "default" : "pointer",
              opacity: 0.85,
            }}>Karte</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSpontaneousSaleSheet — Item ohne Inventar verkaufen
// ─────────────────────────────────────────────────────────────
function FleaSpontaneousSaleSheet({ recentNames, dark, t, onClose, onConfirm, showToast }) {
  const [priceStr, setPriceStr] = useStateF("");
  const [name, setName] = useStateF("");
  const [submitting, setSubmitting] = useStateF(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const priceNum = parseFloat(String(priceStr).replace(",", "."));
  const ready = Number.isFinite(priceNum) && priceNum >= 0;

  const submit = async (payment) => {
    if (!ready || submitting) return;
    if (payment === "card") {
      showToast("Karten-Zahlung kommt mit der nächsten App-Version (SumUp)", "info");
      return;
    }
    setSubmitting(true);
    await onConfirm({ total: priceNum, payment, name: name.trim() });
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Spontan-Verkauf" subtitle="Ein Item ohne Inventar-Eintrag" onClose={onClose} marginBottom={16} />

        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 14, padding: "14px 16px", marginBottom: 12, border: `1px solid ${t.glassBorder}`, display: "flex", alignItems: "center", gap: 10 }}>
          <input type="number" inputMode="decimal" step="0.01" min="0" value={priceStr} onChange={e => setPriceStr(e.target.value)}
            placeholder="0,00"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 28, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
          <span style={{ color: t.textMuted, fontSize: 22, fontWeight: 600 }}>€</span>
        </div>

        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 10, border: `1px solid ${t.glassBorder}` }}>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Bezeichnung (optional)"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "11px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>

        {recentNames.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {recentNames.map(n => (
              <button key={n} onClick={() => setName(n)} style={{
                background: t.accent.blue.weak, color: t.accent.blue.solid,
                border: "none", borderRadius: 8, padding: "5px 10px",
                fontSize: 11, fontWeight: 500, cursor: "pointer", fontFamily: F,
              }}>{n}</button>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <button onClick={() => submit("cash")} disabled={!ready || submitting} style={{
            padding: "14px",
            background: ready ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: ready && !submitting ? "pointer" : "not-allowed",
          }}>Bar</button>
          <button onClick={() => submit("card")} disabled={!ready || submitting} style={{
            padding: "14px",
            background: ready ? t.accent.blue.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: ready && !submitting ? "pointer" : "not-allowed",
            opacity: 0.85,
          }}>Karte</button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaAddItemsSheet — mid-session Items aus Inventar dazunehmen
// + optional sofort ein neues Item anlegen (Schnellanlage)
// ─────────────────────────────────────────────────────────────
function FleaAddItemsSheet({ store, authToken, dark, t, items, selectedItemIds, onClose, onAdd, onItemCreated, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [picked, setPicked] = useStateF(new Set());
  const [submitting, setSubmitting] = useStateF(false);
  const [filter, setFilter] = useStateF("");
  const [quickName, setQuickName] = useStateF("");
  const [quickPrice, setQuickPrice] = useStateF("");
  const [creating, setCreating] = useStateF(false);

  // Items die NICHT bereits in der Session sind und aktiv sind
  const candidates = useMemoF(() => {
    const f = filter.trim().toLowerCase();
    return items
      .filter(it => it.active !== false && !selectedItemIds.has(it.id))
      .filter(it => !f || it.name.toLowerCase().includes(f) || (it.description || "").toLowerCase().includes(f))
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [items, selectedItemIds, filter]);

  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
    haptic(6);
  };

  const submit = async () => {
    if (picked.size === 0 || submitting) return;
    setSubmitting(true);
    await onAdd([...picked]);
    setSubmitting(false);
  };

  // Schnellanlage: legt neues Item an UND fügt es direkt zur Session hinzu.
  const quickCreate = async () => {
    const name = quickName.trim();
    const priceNum = parseFloat(String(quickPrice).replace(",", "."));
    if (!name || !Number.isFinite(priceNum) || priceNum < 0 || creating) return;
    setCreating(true);
    try {
      const created = await apiCall(authToken, `/api/stores/${store.id}/items`, {
        method: "POST",
        body: { name, wish_price: priceNum },
      });
      onItemCreated(created);
      setQuickName("");
      setQuickPrice("");
      haptic(15);
      // Direkt zur Session hinzufügen
      await onAdd([created.id]);
    } catch (e) {
      showToast(e.message || "Anlegen fehlgeschlagen", "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 460, radius: 24, padding: 0, zIndex: 93 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Items hinzunehmen"
            subtitle={picked.size > 0 ? `${picked.size} ausgewählt` : "Aus Inventar oder neu anlegen"}
            onClose={onClose} marginBottom={12} />
        </div>

        {/* Schnellanlage */}
        <div style={{ padding: "0 22px 12px", borderBottom: `0.5px solid ${t.divider}` }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
            Schnellanlage
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input type="text" value={quickName} onChange={e => setQuickName(e.target.value)}
              placeholder="Item-Name"
              style={{
                flex: 2, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                border: `1px solid ${t.glassBorder}`, borderRadius: 10,
                padding: "10px 12px", fontSize: 14, color: t.text, fontFamily: F, outline: "none", minWidth: 0,
              }}/>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={quickPrice} onChange={e => setQuickPrice(e.target.value)}
              placeholder="Preis €"
              style={{
                flex: 1, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                border: `1px solid ${t.glassBorder}`, borderRadius: 10,
                padding: "10px 12px", fontSize: 14, color: t.text, fontFamily: F, outline: "none", minWidth: 0,
                textAlign: "right",
              }}/>
            <button onClick={quickCreate} disabled={!quickName.trim() || !Number.isFinite(parseFloat(String(quickPrice).replace(",", "."))) || creating} style={{
              padding: "10px 14px",
              background: quickName.trim() && Number.isFinite(parseFloat(String(quickPrice).replace(",", "."))) && !creating ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
              color: quickName.trim() && Number.isFinite(parseFloat(String(quickPrice).replace(",", "."))) && !creating ? "#fff" : t.textMuted,
              border: "none", borderRadius: 10,
              fontFamily: F, fontWeight: 700, fontSize: 14,
              cursor: quickName.trim() && Number.isFinite(parseFloat(String(quickPrice).replace(",", "."))) && !creating ? "pointer" : "not-allowed",
              flexShrink: 0,
            }}>{creating ? "…" : "+"}</button>
          </div>
        </div>

        {/* Liste der Inventar-Items, die noch nicht dabei sind */}
        <div style={{ padding: "12px 22px 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", paddingLeft: 4 }}>
              Inventar · {candidates.length}
            </div>
            {picked.size > 0 && (
              <button onClick={() => { setPicked(new Set()); haptic(6); }} style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", fontFamily: F }}>Keine</button>
            )}
          </div>
          {items.filter(it => it.active !== false && !selectedItemIds.has(it.id)).length > 5 && (
            <div style={{ marginBottom: 8, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: "0 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: t.textMuted, flexShrink: 0 }}>
                <circle cx="6" cy="6" r="4"/><path d="m12 12-3-3" strokeLinecap="round"/>
              </svg>
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtern …"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 13, color: t.text, fontFamily: F, minWidth: 0 }}/>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 22px", maxHeight: Math.max(160, vh - 460) }}>
          {candidates.length === 0 ? (
            <div style={{ textAlign: "center", padding: 16, color: t.textMuted, fontSize: 13 }}>
              {filter ? "Nichts gefunden." : "Alle Inventar-Items sind schon dabei."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 12 }}>
              {candidates.map(it => {
                const sel = picked.has(it.id);
                return (
                  <button key={it.id} onClick={() => toggle(it.id)} style={{
                    width: "100%",
                    background: sel ? t.accent.purple.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                    border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                    borderRadius: 11, padding: "10px 12px",
                    cursor: "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10,
                    transition: "all 0.14s ease",
                  }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `1.5px solid ${sel ? t.accent.purple.solid : t.glassBorder}`,
                      background: sel ? t.accent.purple.solid : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {sel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: sel ? 600 : 500, color: sel ? t.accent.purple.solid : t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</span>
                    <span style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(it.wish_price)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 22px calc(env(safe-area-inset-bottom, 14px) + 14px)", borderTop: `0.5px solid ${t.divider}` }}>
          <button onClick={submit} disabled={picked.size === 0 || submitting} style={{
            width: "100%", padding: "13px",
            background: picked.size > 0 ? t.accent.purple.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: picked.size > 0 ? "#fff" : t.textMuted,
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: picked.size > 0 && !submitting ? "pointer" : "not-allowed",
            boxShadow: picked.size > 0 ? "0 4px 16px rgba(191,90,242,0.35)" : "none",
          }}>
            {submitting ? "Füge hinzu…" : picked.size === 0 ? "Items wählen" : `${picked.size} ${picked.size === 1 ? "Item" : "Items"} hinzunehmen`}
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSaleStornoSheet — Verkauf stornieren
// ─────────────────────────────────────────────────────────────
function FleaSaleStornoSheet({ sale, dark, t, onClose, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useStateF(false);
  const items = sale.sale_items || [];
  const isVoided = sale.is_voided;

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 95 })}>
        <ModalHeader t={t} dark={dark} F={F} title={isVoided ? "Verkauf storniert" : "Verkauf-Detail"} onClose={onClose} marginBottom={14} />

        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)", borderRadius: 12, padding: "14px 16px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", marginBottom: 4 }}>
            {fmtCurrency(sale.total)}
          </div>
          <div style={{ fontSize: 12, color: t.textSub }}>
            {fmtTime(sale.sold_at)} · {sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}
          </div>
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          {sale.is_bundle ? `Items (${items.length})` : "Item"}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
          {items.map((si, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)", borderRadius: 9, border: `1px solid ${t.glassBorder}` }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {si.item_name}{!si.item_id && <span style={{ color: t.textMuted }}> (Spontan)</span>}
              </span>
              {si.wish_price != null && (
                <span style={{ fontSize: 11, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>Wunsch {fmtCurrency(si.wish_price)}</span>
              )}
            </div>
          ))}
        </div>

        {!isVoided && (
          <button onClick={async () => { setSubmitting(true); await onConfirm(); setSubmitting(false); }} disabled={submitting} style={{
            width: "100%", padding: "13px",
            background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}40`, borderRadius: 12,
            color: t.accent.red.solid, fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1,
          }}>Verkauf stornieren</button>
        )}
        {isVoided && (
          <div style={{ textAlign: "center", padding: 14, fontSize: 13, color: t.textMuted }}>
            Wurde am {fmtTime(sale.voided_at)} storniert.
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSessionEndSheet — Markt beenden / Tagesabschluss
// ─────────────────────────────────────────────────────────────
function FleaSessionEndSheet({ store, authToken, dark, t, session, sales, items, selectedItemIds, soldItemIds, onClose, onEnded, showToast }) {
  const [restbestandAction, setRestbestandAction] = useStateF("keep"); // 'keep' | 'deactivate'
  const [notes, setNotes] = useStateF("");
  const [submitting, setSubmitting] = useStateF(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const stats = computeSessionStats(sales);
  const restItems = useMemoF(() =>
    items.filter(it => selectedItemIds.has(it.id) && !soldItemIds.has(it.id) && it.active !== false)
  , [items, selectedItemIds, soldItemIds]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const apiBase = `/api/stores/${store.id}`;
      // 1. Session beenden
      const updatedSession = await apiCall(authToken, `${apiBase}/sessions/${session.id}`, {
        method: "PATCH",
        body: {
          ends_at: new Date().toISOString(),
          notes: notes.trim() || null,
          metadata: { ...(session.metadata || {}), restbestand_action: restbestandAction },
        },
      });
      // 2. Optional: Restbestand auf inaktiv setzen
      let updatedItems = null;
      if (restbestandAction === "deactivate" && restItems.length > 0) {
        await Promise.all(restItems.map(it =>
          apiCall(authToken, `${apiBase}/items/${it.id}`, { method: "DELETE" })
        ));
        updatedItems = items.map(it => restItems.find(r => r.id === it.id) ? { ...it, active: false } : it);
      }
      haptic(20);
      showToast("Markt beendet");
      onEnded(updatedSession, updatedItems);
    } catch (e) {
      showToast(e.message || "Beenden fehlgeschlagen", "error");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 94,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 460, radius: 24, padding: 0, zIndex: 95 })}>
        <div style={{ padding: "20px 22px 16px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Markt beenden" subtitle={session.location || "Trödel-Markt"} onClose={onClose} marginBottom={16} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 22px" }}>
          {/* Tagesstatistik-Block */}
          <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)", borderRadius: 14, padding: "16px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Tages-Umsatz</span>
              <span style={{ fontSize: 12, color: t.textSub }}>{fmtDuration(session.starts_at, new Date().toISOString())}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", marginBottom: 8 }}>
              {fmtCurrency(stats.total)}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12, color: t.textSub }}>
              <span>{stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}</span>
              {stats.bundleCount > 0 && <span>· {stats.bundleCount} Bundle</span>}
              {stats.cash > 0 && <span>· Bar {fmtCurrency(stats.cash)}</span>}
              {stats.card > 0 && <span>· Karte {fmtCurrency(stats.card)}</span>}
            </div>
            {stats.wishSum > 0 && stats.count > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.divider}`, fontSize: 12, color: t.textSub }}>
                Wunsch {fmtCurrency(stats.wishSum)} · verkauft für {fmtCurrency(stats.total)} ·{" "}
                <span style={{ color: stats.diff < 0 ? t.accent.orange.solid : t.accent.green.solid, fontWeight: 600 }}>
                  {stats.diff > 0 ? "+" : ""}{fmtCurrency(stats.diff)} ({stats.diffPct > 0 ? "+" : ""}{stats.diffPct}%)
                </span>
              </div>
            )}
          </div>

          {/* Restbestand */}
          {restItems.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
                Restbestand · {restItems.length} unverkauft
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                {[
                  { id: "keep", label: "Im Inventar lassen", desc: "Items bleiben aktiv für den nächsten Markt" },
                  { id: "deactivate", label: "Auf inaktiv setzen", desc: "Als verkauft/weggeworfen markieren" },
                ].map(opt => {
                  const sel = restbestandAction === opt.id;
                  return (
                    <button key={opt.id} onClick={() => { setRestbestandAction(opt.id); haptic(6); }} style={{
                      width: "100%",
                      background: sel ? t.accent.blue.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                      border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                      borderRadius: 11, padding: "12px 14px",
                      cursor: "pointer", fontFamily: F, textAlign: "left",
                      display: "flex", alignItems: "center", gap: 10,
                    }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                        border: `1.5px solid ${sel ? t.accent.blue.solid : t.glassBorder}`,
                        background: sel ? t.accent.blue.solid : "transparent",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {sel && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: sel ? t.accent.blue.solid : t.text }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>{opt.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Notiz */}
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>Notiz</div>
          <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Wetter, Standgebühr, Highlights …"
              rows={2}
              style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 13, color: t.text, fontFamily: F, resize: "none", minWidth: 0 }}/>
          </div>
        </div>

        <div style={{ padding: "12px 22px calc(env(safe-area-inset-bottom, 14px) + 14px)", borderTop: `0.5px solid ${t.divider}` }}>
          <button onClick={submit} disabled={submitting} style={{
            width: "100%", padding: "14px",
            background: t.accent.red.solid,
            border: "none", borderRadius: 14,
            color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1,
            boxShadow: `0 6px 22px ${t.accent.red.weak}`,
          }}>{submitting ? "Beende…" : "Markt abschließen"}</button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaSessionDetail — Detail-Ansicht vergangener Markt-Tag
// ─────────────────────────────────────────────────────────────
function FleaSessionDetail({ store, authToken, dark, t, sessionId, items, showToast, onBack, onSessionChanged }) {
  const [detail, setDetail] = useStateF(null);
  const [stornoSale, setStornoSale] = useStateF(null);
  const [confirmStorno, setConfirmStorno] = useStateF(null);
  const [error, setError] = useStateF(null);

  useEffectF(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiCall(authToken, `/api/stores/${store.id}/sessions/${sessionId}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Lade-Fehler");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const performStorno = async (sale) => {
    try {
      const updated = await apiCall(authToken, `/api/stores/${store.id}/sales/${sale.id}`, { method: "PATCH", body: { is_voided: true } });
      setDetail(prev => prev ? {
        ...prev,
        sales: prev.sales.map(s => s.id === sale.id ? { ...s, ...updated, sale_items: s.sale_items } : s),
      } : prev);
      showToast("Verkauf storniert");
      haptic(15);
      onSessionChanged?.();
    } catch (e) {
      showToast(e.message || "Storno fehlgeschlagen", "error");
    }
  };

  const bg = dark ? "#0d1117" : "#dde8f8";

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: F, color: t.text, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: t.accent.red.solid, marginBottom: 12 }}>{error}</div>
          <button onClick={onBack} style={{ background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontFamily: F, fontWeight: 600, fontSize: 13 }}>Zurück</button>
        </div>
      </div>
    );
  }
  if (!detail) {
    return <div style={{ minHeight: "100vh", background: bg, fontFamily: F, display: "flex", alignItems: "center", justifyContent: "center", color: t.textMuted, fontSize: 14 }}>Lade…</div>;
  }

  const sales = detail.sales || [];
  const stats = computeSessionStats(sales);
  const sortedSales = [...sales].sort((a, b) => (b.sold_at || "").localeCompare(a.sold_at || ""));

  return (
    <div style={{
      height: "100dvh", maxHeight: "100dvh",
      background: bg, fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <button onClick={onBack} aria-label="Zurück" style={{
          width: 30, height: 30, borderRadius: 14, flexShrink: 0,
          background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
          border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {detail.location || "Trödel-Markt"}
          </div>
          <div style={{ fontSize: 12, color: t.textSub }}>
            {fmtDateShort(detail.starts_at)} · {fmtDuration(detail.starts_at, detail.ends_at)}
            {!detail.ends_at && " · LIVE"}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)" }}>
        {/* Stats */}
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)", borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <div style={{ fontSize: 32, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", marginBottom: 6 }}>
            {fmtCurrency(stats.total)}
          </div>
          <div style={{ fontSize: 12, color: t.textSub, display: "flex", flexWrap: "wrap", gap: 10 }}>
            <span>{stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}</span>
            {stats.bundleCount > 0 && <span>· {stats.bundleCount} Bundle</span>}
            {stats.cash > 0 && <span>· Bar {fmtCurrency(stats.cash)}</span>}
            {stats.card > 0 && <span>· Karte {fmtCurrency(stats.card)}</span>}
          </div>
          {stats.wishSum > 0 && stats.count > 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `0.5px solid ${t.divider}`, fontSize: 12, color: t.textSub }}>
              Wunsch {fmtCurrency(stats.wishSum)} ·{" "}
              <span style={{ color: stats.diff < 0 ? t.accent.orange.solid : t.accent.green.solid, fontWeight: 600 }}>
                {stats.diff > 0 ? "+" : ""}{fmtCurrency(stats.diff)} ({stats.diffPct > 0 ? "+" : ""}{stats.diffPct}%)
              </span>
            </div>
          )}
        </div>

        {/* Notes */}
        {detail.notes && (
          <div style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)", borderRadius: 12, padding: "12px 14px", marginBottom: 14, fontSize: 13, color: t.textSub, lineHeight: 1.5, fontStyle: "italic" }}>
            {detail.notes}
          </div>
        )}

        {/* Sales-Liste */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
          Alle Verkäufe · {sales.length}
        </div>
        {sortedSales.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted, fontSize: 13 }}>Keine Verkäufe an diesem Tag.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sortedSales.map(sale => {
              const isVoided = sale.is_voided;
              const items = sale.sale_items || [];
              const headline = sale.is_bundle ? `Bundle (${items.length})` : (items[0]?.item_name || "Verkauf");
              return (
                <button key={sale.id} onClick={() => setStornoSale(sale)} style={{
                  width: "100%",
                  background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
                  border: `1px solid ${t.glassBorder}`,
                  borderRadius: 12, padding: "10px 12px",
                  cursor: "pointer", fontFamily: F, textAlign: "left",
                  display: "flex", alignItems: "center", gap: 10,
                  opacity: isVoided ? 0.5 : 1,
                  textDecoration: isVoided ? "line-through" : "none",
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: isVoided ? t.accent.red.weak : t.accent.green.weak,
                    color: isVoided ? t.accent.red.solid : t.accent.green.solid,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isVoided ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{headline}</div>
                    <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
                      {fmtTime(sale.sold_at)} · {sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(sale.total)}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {stornoSale && !confirmStorno && (
        <FleaSaleStornoSheet
          sale={stornoSale} dark={dark} t={t}
          onClose={() => setStornoSale(null)}
          onConfirm={async () => {
            // Bei vergangenen Sessions: zusätzliche Bestätigung
            if (detail.ends_at) {
              setConfirmStorno(stornoSale);
            } else {
              await performStorno(stornoSale);
              setStornoSale(null);
            }
          }}
        />
      )}
      {confirmStorno && (
        <ConfirmSheet
          dark={dark} t={t}
          title="Verkauf wirklich stornieren?"
          subtitle="Tagesstatistik wird angepasst"
          warning="Dieser Markt-Tag ist bereits abgeschlossen. Storno passt die historische Statistik an."
          confirmLabel="Stornieren"
          confirmDestructive={true}
          onCancel={() => { setConfirmStorno(null); setStornoSale(null); }}
          onConfirm={async () => {
            await performStorno(confirmStorno);
            setConfirmStorno(null);
            setStornoSale(null);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FleaStats — Tab "Statistik"
// ─────────────────────────────────────────────────────────────
function FleaStats({ sessions, items, dark, t }) {
  // P-Polish 1.2/1.7/1.10/1.13:
  // Sessions kommen mit embedded sales (Worker include_stats=1) → wir haben
  // total + is_voided und können client-side aggregieren.

  const completedSessions = sessions.filter(s => s.ends_at);
  const allSessions = sessions.length;

  // Wochentag-Toggle (P-Polish 1.10): "count" oder "revenue"
  const [weekdayMetric, setWeekdayMetric] = useStateF("count");

  // Per-Session aggregierte Werte (count + revenue) aus eingebetteten sales
  const sessionAgg = (s) => {
    const sales = (s.sales || []).filter(x => !x.is_voided);
    return {
      count: sales.length,
      revenue: sales.reduce((sum, x) => sum + Number(x.total || 0), 0),
    };
  };

  // Gesamt-Umsatz über alle Sessions (P-Polish 1.2)
  const totalRevenue = useMemoF(() =>
    sessions.reduce((sum, s) => sum + sessionAgg(s).revenue, 0)
  , [sessions]);
  const totalCount = useMemoF(() =>
    sessions.reduce((sum, s) => sum + sessionAgg(s).count, 0)
  , [sessions]);

  // Top-Orte mit Umsatz (P-Polish 1.7): sortiert nach Umsatz absteigend
  const topLocations = useMemoF(() => {
    const map = new Map();
    for (const s of sessions) {
      if (!s.location) continue;
      const cur = map.get(s.location) || { count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += sessionAgg(s).revenue;
      map.set(s.location, cur);
    }
    return [...map.entries()]
      .map(([loc, v]) => ({ location: loc, ...v }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [sessions]);

  // Wochentag-Verteilung (count UND revenue)
  const weekdayCounts = useMemoF(() => {
    const days = ["So","Mo","Di","Mi","Do","Fr","Sa"];
    const counts = [0,0,0,0,0,0,0];
    const revenues = [0,0,0,0,0,0,0];
    for (const s of sessions) {
      const d = new Date(s.starts_at);
      counts[d.getDay()]++;
      revenues[d.getDay()] += sessionAgg(s).revenue;
    }
    return days.map((label, i) => ({ label, count: counts[i], revenue: revenues[i] }));
  }, [sessions]);
  const maxValueByMetric = Math.max(
    ...weekdayCounts.map(w => weekdayMetric === "count" ? w.count : w.revenue),
    1
  );

  // P-Polish 1.13: Detail-Statistiken-Schwelle = 5 abgeschlossene Markt-Tage
  const DETAIL_THRESHOLD = 5;
  const remaining = Math.max(0, DETAIL_THRESHOLD - completedSessions.length);

  return (
    <>
      {/* Hero — P-Polish 1.2: Gesamt-Umsatz oben prominent */}
      <div style={{
        background: "linear-gradient(180deg, rgba(191,90,242,0.08) 0%, rgba(191,90,242,0.03) 100%)",
        border: `1px solid ${t.accent.purple.solid}30`,
        borderRadius: 14, padding: "18px 18px 16px",
        marginBottom: 14,
        textAlign: "center",
      }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.5px", lineHeight: 1 }}>
          {fmtCurrency(totalRevenue)}
        </div>
        <div style={{ fontSize: 12, color: t.textSub, marginTop: 4 }}>
          Gesamt · {allSessions} {allSessions === 1 ? "Markt-Tag" : "Markt-Tage"}
          {totalCount > 0 && ` · ${totalCount} ${totalCount === 1 ? "Verkauf" : "Verkäufe"}`}
        </div>
        {allSessions - completedSessions.length > 0 && (
          <div style={{ fontSize: 11, color: t.accent.purple.solid, fontWeight: 600, marginTop: 4 }}>
            {allSessions - completedSessions.length} aktiv · {completedSessions.length} abgeschlossen
          </div>
        )}
      </div>

      {/* Top-Orte — P-Polish 1.7: Umsatz statt nur Anzahl */}
      {topLocations.length > 0 && (
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)", borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Top-Orte</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topLocations.map(l => (
              <div key={l.location} style={{ display: "flex", alignItems: "center", padding: "6px 0", gap: 10 }}>
                <span style={{ fontSize: 13, color: t.text, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{l.location}</span>
                <span style={{ fontSize: 11, color: t.textSub, flexShrink: 0 }}>
                  {l.count} {l.count === 1 ? "Markt" : "Märkte"}
                </span>
                <span style={{ fontSize: 13, color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                  {fmtCurrency(l.revenue)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Wochentag-Verteilung mit Toggle (P-Polish 1.10) */}
      {sessions.length > 0 && (
        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)", borderRadius: 14, padding: 16, marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>Wochentage</div>
            {/* Toggle: count vs. revenue */}
            <div style={{ display: "flex", gap: 4, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", borderRadius: 8, padding: 2 }}>
              {[
                { id: "count",   label: "Anzahl" },
                { id: "revenue", label: "Umsatz" },
              ].map(opt => {
                const active = weekdayMetric === opt.id;
                return (
                  <button key={opt.id} onClick={() => { setWeekdayMetric(opt.id); haptic(6); }} style={{
                    padding: "5px 10px", border: "none", borderRadius: 6,
                    background: active ? (dark ? "rgba(255,255,255,0.14)" : "#fff") : "transparent",
                    color: active ? t.text : t.textMuted,
                    fontFamily: F, fontWeight: active ? 700 : 500, fontSize: 11,
                    cursor: "pointer",
                    boxShadow: active ? `0 1px 3px rgba(0,0,0,${dark ? 0.3 : 0.06})` : "none",
                  }}>{opt.label}</button>
                );
              })}
            </div>
          </div>
          {/* Y-Achse-Beschriftung + Bars */}
          <div style={{ display: "flex", alignItems: "stretch", gap: 8, height: 110 }}>
            <div style={{
              display: "flex", flexDirection: "column", justifyContent: "space-between",
              fontSize: 9, color: t.textMuted, fontVariantNumeric: "tabular-nums",
              paddingTop: 12, paddingBottom: 18, flexShrink: 0, minWidth: 30, textAlign: "right",
            }}>
              <span>{weekdayMetric === "count"
                ? maxValueByMetric
                : Math.round(maxValueByMetric).toLocaleString("de-DE") + " €"}</span>
              <span>0</span>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 6 }}>
              {weekdayCounts.map(w => {
                const v = weekdayMetric === "count" ? w.count : w.revenue;
                const h = v > 0 ? Math.max(8, (v / maxValueByMetric) * 80) : 4;
                return (
                  <div key={w.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ fontSize: 9, color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums", height: 12, opacity: v > 0 ? 1 : 0, whiteSpace: "nowrap" }}>
                      {weekdayMetric === "count" ? w.count : (w.revenue >= 100 ? Math.round(w.revenue) + "" : w.revenue.toFixed(0))}
                    </div>
                    <div style={{ width: "100%", height: h, background: v > 0 ? t.accent.purple.solid : t.glassBorder, borderRadius: "4px 4px 0 0", transition: "height 0.4s ease" }}/>
                    <div style={{ fontSize: 10, color: t.textSub, fontWeight: 500 }}>{w.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Detail-Statistiken-Hint — P-Polish 1.13: Bedingung explizit machen */}
      {remaining > 0 ? (
        <div style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)", borderRadius: 12, padding: "12px 14px", fontSize: 12, color: t.textSub, lineHeight: 1.5 }}>
          {remaining === DETAIL_THRESHOLD
            ? `Detail-Statistiken (Verhandlungs-Schnitt, Top-Items, Trends) erscheinen ab ${DETAIL_THRESHOLD} abgeschlossenen Markt-Tagen.`
            : remaining === 1
              ? "Noch 1 Markt-Tag bis Detail-Statistiken erscheinen."
              : `Noch ${remaining} Markt-Tage bis Detail-Statistiken erscheinen.`}
        </div>
      ) : null}
    </>
  );
}

export { FleaHub };
